// Menu tree mirrors MARG ERP 9+ (Silver): the top bar from the real screens, with the
// Level 2 → 3 → 4 structure of each subsystem (Alt+E Masters, Alt+T Transactions,
// Alt+B Books, Alt+S Stocks, Alt+D Daily Reports, Alt+R Reports, Alt+G GST).
// `hot` is the underlined access letter; items without an `action` are placeholders
// that show "404 — NOT EXIST" when chosen.
export interface MenuItem {
  label: string;
  hot?: string;
  shortcut?: string;
  action?: string;
  children?: MenuEntry[];
  bold?: boolean;
  isNew?: boolean;
  disabled?: boolean;
  icon?: string; // small picture shown left of the label (Digital menu)
}
export type MenuEntry = MenuItem | '-';

// Reports → Sale Analysis → M.R. / Route / Area Wise Sale (same layout, different name)
const regionSaleMenu = (r: string): MenuEntry[] => [
  { label: 'Item/Month Wise', hot: 'I' },
  { label: 'Company/Month Wise', hot: 'C' },
  '-',
  { label: `All ${r}/Month`, hot: 'A', action: r === 'M.R.' ? 'report.mrsale:month' : undefined },
  { label: `${r} Wise Sale`, hot: 'W', action: r === 'M.R.' ? 'report.mrsale:summary' : undefined },
  { label: `Single ${r} Sale`, hot: 'S', action: r === 'M.R.' ? 'report.mrsale:single' : undefined },
  '-',
  { label: `${r}/All Items`, hot: 't', action: r === 'M.R.' ? 'report.mrsale:items' : undefined },
  '-',
  { label: 'Sale/Collection Analysis', hot: 'y' },
  { label: 'Sale/Collection/O/s', hot: 'O', action: 'report.outstanding:receivable' },
  { label: 'Bill Collection Ageing', hot: 'g' },
  '-',
  { label: 'Bill Wise Sale & Cost', hot: 'B', action: 'report.gp' },
];
// Reports → Management Report → Gross Profit Analysis → M.R. / Route / Area G.P. Summaries
const regionGpMenu = (r: string, wise: string): MenuEntry[] => [
  { label: 'Item/Month Wise', hot: 'I' },
  { label: 'Company/Month Wise', hot: 'C' },
  '-',
  { label: `All ${r}/Month`, hot: 'A' },
  { label: wise, hot: 'W' },
  { label: `Single ${r} G.P.`, hot: 'S' },
  '-',
  { label: `${r}/All Items`, hot: 't' },
];
// Company Wise Sale (Sale Analysis) and Company G.P. Summary (Gross Profit Analysis)
const companySaleMenu = (kind: 'Sale' | 'G.P.'): MenuEntry[] => [
  { label: 'Party/Month Wise', hot: 'P' },
  { label: 'M.R./Month Wise', hot: 'M' },
  { label: 'Area/Month Wise', hot: 'A' },
  { label: 'Route/Month Wise', hot: 'R' },
  { label: 'Items/Month Wise', hot: 'I' },
  '-',
  { label: 'All Company/Month', hot: 'l' },
  { label: `Company Wise ${kind}`, hot: 'C', action: kind === 'Sale' ? 'report.company_sale' : undefined },
  { label: `Single Company ${kind}`, hot: 'S', action: kind === 'Sale' ? 'report.single_company_sale' : undefined },
  '-',
  { label: `Supplier Wise ${kind}`, hot: 'u' },
  '-',
  { label: 'Company Sale Book', hot: 'B' },
  { label: 'Company Sale/Purchase', hot: 'e' },
  '-',
  { label: 'Parties/All Company', hot: 't' },
  { label: 'M.R./All Company', hot: '.' },
  { label: "Area's/All Company", hot: 'r' },
  { label: "Route's/All Company", hot: 'o' },
  '-',
  { label: kind === 'Sale' ? 'Route/Area/M.R.Sale' : 'Route/Area/M.R. Sale', hot: 'a' },
];
// Submenus whose contents haven't been captured from MARG yet.
const notShownYet = (): MenuEntry[] => [{ label: '(not available yet)', disabled: true }];
const newModify = (): MenuEntry[] => [{ label: 'New', hot: 'N' }, { label: 'Modify', hot: 'M' }];
// Purchase Return / Brk-Exp Issue → Quick Issue
const quickIssueMenu = (): MenuEntry[] => [
  { label: 'Manual Return', hot: 'M' },
  '-',
  { label: 'Quick D/Note', hot: 'D', action: 'bill.new:PURCHASE_RETURN' },
  { label: 'Quick Challan', hot: 'C' },
  '-',
  { label: 'Expiry Return', hot: 'E' },
  { label: 'Near Expiry', hot: 'N' },
  { label: 'Dump Return', hot: 'u' },
];
// Sales Order / Purchase Order (the purchase side adds E-Mail Pending)
const orderMenu = (purchase: boolean): MenuEntry[] => [
  { label: 'New', hot: 'N' },
  { label: 'Modify', hot: 'M' },
  '-',
  ...(purchase ? [{ label: 'E-Mail Pending', hot: 'E', bold: true, isNew: true }] : []),
  { label: 'Order History', hot: 'O' },
  { label: 'Summary', hot: 'S' },
  { label: 'Item Summary', hot: 'I' },
  { label: 'Party Summary', hot: 'P' },
  '-',
  { label: 'Party Pending', hot: 'a' },
  { label: 'Item Pending', hot: 't' },
  '-',
  { label: 'Order Analysis', hot: 'y' },
  { label: 'Delete Old Orders', hot: 'D' },
];

const LINKS_MENU: MenuItem = {
  label: 'Links', hot: 'L', children: [
    { label: 'Website', hot: 'W' },
    { label: 'Tutorials', hot: 'T' },
    { label: 'YouTube', hot: 'Y' },
    { label: 'Academy', hot: 'A' },
    { label: 'Partnership', hot: 'P' },
    { label: 'Facebook', hot: 'F' },
    { label: 'Boosters', hot: 'B' },
    '-',
    {
      label: 'Downloads', hot: 'D', children: [
        { label: 'Download Page', hot: 'D' },
        '-',
        { label: 'All Updates', hot: 'A' },
        { label: 'Image Ads', hot: 'I' },
      ],
    },
    '-',
    { label: 'Contact Us', hot: 'C' },
  ],
};

const EXIT_MENU: MenuItem = {
  label: 'Exit', hot: 'x', children: [
    { label: 'Flow Ledger Help', hot: 'M', action: 'shortcuts' },
    { label: 'About Flow Ledger', hot: 'A' },
    { label: 'Remote Support', hot: 'R' },
    { label: 'Join Webinar', hot: 'J' },
    { label: 'Save Passwords', hot: 'S' },
    '-',
    { label: 'Update Dashboard', hot: 'U' },
    { label: 'Change Image', hot: 'C' },
    '-',
    { label: "Registered DLL's", hot: 'R' },
    { label: 'Exit', hot: 'E', shortcut: 'ALT+X', action: 'company.change' },
  ],
};

export const MENU: MenuItem[] = [
  // ====================================================================== MASTERS (Alt+E)
  {
    label: 'Masters', hot: 'e', children: [
      {
        label: 'Ledger Master', hot: 'L', children: [
          {
            label: 'Ledger', hot: 'L', children: [
              { label: 'New Ledger', hot: 'N', action: 'ledger.add' },
              { label: 'Modify Ledger', hot: 'M', action: 'ledger.list' },
              { label: 'Delete Ledger', hot: 'D', action: 'ledger.delete' },
            ],
          },
          '-',
          {
            label: 'Sale', hot: 'S', children: [
              { label: 'New Sale', hot: 'N', action: 'ledger.add' },
              { label: 'Modify Sale', hot: 'M', action: 'ledger.list:Sundry Debtors' },
              { label: 'Delete Sale', hot: 'D', action: 'ledger.delete:Sundry Debtors' },
            ],
          },
          {
            label: 'Purchase', hot: 'P', children: [
              { label: 'New Purchase', hot: 'N', action: 'ledger.add' },
              { label: 'Modify Purchase', hot: 'M', action: 'ledger.list:Sundry Creditors' },
              { label: 'Delete Purchase', hot: 'D', action: 'ledger.delete:Sundry Creditors' },
            ],
          },
          '-',
          { label: 'Budget', hot: 'B' },
          {
            label: 'Targets', hot: 'T', children: [
              { label: 'Target Customers', hot: 'C' },
              { label: 'Target Field Force', hot: 'F' },
            ],
          },
          '-',
          { label: 'List of Account', hot: 'i', action: 'ledger.list' },
          { label: 'Ledger Export', hot: 'E', action: 'ledger.export' },
          { label: 'Ledger Import', hot: 'm' },
          { label: 'Party GST No./State', hot: 'G', action: 'ledger.gst' },
          { label: 'Party GEO Location', hot: 'y' },
        ],
      },
      {
        label: 'Account Groups', hot: 'A', children: [
          { label: 'New Group', hot: 'N', action: 'group.add' },
          { label: 'Modify Group', hot: 'M', action: 'group.list' },
          { label: 'Delete Group', hot: 'D', action: 'group.list' },
          '-',
          { label: 'Budget', hot: 'B' },
        ],
      },
      {
        label: 'Master', hot: 'M', children: [
          { label: 'Item Master', hot: 'I', action: 'item.list' },
          { label: 'Item Maping', hot: 'p' },
          '-',
          { label: 'Store Master', hot: 'S' },
          { label: 'Company Master', hot: 'C' },
          { label: 'Salt Master', hot: 'a' },
          { label: 'HSN/SAC Master', hot: 'H', action: 'item.list' },
          '-',
          { label: 'Area Master', hot: 'A' },
          { label: 'Route Master', hot: 'R' },
          { label: 'Station', hot: 't' },
          { label: 'State', hot: 'e', action: 'ledger.gst' },
          { label: 'Country', hot: 'o' },
          '-',
          { label: 'Floor wise Store Room', hot: 'F' },
          { label: 'Block Store Room', hot: 'B' },
          '-',
          { label: 'Block Email ID', hot: 'l' },
          { label: 'Block Message on Mobile', hot: 'g' },
        ],
      },
      {
        label: 'Rate & Discount Master', hot: 'R', children: [
          {
            label: 'Price List', hot: 'P', children: [
              { label: 'All Items', hot: 'A', action: 'item.prices' },
              { label: 'Selected Company/Item', hot: 'S', action: 'item.prices:company' },
              { label: 'Selected Salt', hot: 'l' },
              { label: 'Area Wise', hot: 'r' },
              '-',
              { label: 'Import From Excel', hot: 'I', bold: true },
              { label: 'New Item Import Method', hot: 'N' },
            ],
          },
          {
            label: 'Company Special', hot: 'C', children: [
              { label: 'Company General Discount', hot: 'G' },
              { label: 'Company wise Party Discount', hot: 'P' },
              { label: 'Company V/s Area Ban', hot: 'A' },
              { label: 'Rate wise Company Wise GST', hot: 'R' },
            ],
          },
          {
            label: 'Party Special', hot: 'a', children: [
              { label: 'Party Categorisation for Discounts', hot: 'C' },
              {
                label: 'Party Price List', hot: 'P', children: [
                  { label: 'All Items', hot: 'A', action: 'item.list' },
                  { label: 'Selected Company/Item', hot: 'S', action: 'item.list' },
                ],
              },
              { label: 'All Party General Discount', hot: 'A' },
              { label: 'Party wise Company Discount', hot: 'w' },
              { label: 'Item/Party Special Price', hot: 'I' },
            ],
          },
          {
            label: 'Maximum Settings', hot: 'x', children: [
              { label: 'Maximum Company Discount', hot: 'C' },
              { label: 'Maximum Item Discount', hot: 'I' },
              { label: 'Maximum Qty.Allowed in Bill', hot: 'Q' },
            ],
          },
          {
            label: 'Bill,Date & Other', hot: 'B', children: [
              { label: 'Date wise Rate/Scheme', hot: 'D' },
              { label: 'Date wise Rate/Scheme Area Wise', hot: 'A' },
              '-',
              { label: 'Bill Value wise Discount', hot: 'B' },
              { label: 'Bill Value wise Schemes', hot: 'S' },
            ],
          },
          '-',
          {
            label: "Sales Booster's", hot: 'S', children: [
              { label: 'Focused Items', hot: 'F' },
              { label: 'PUSH SALE Items', hot: 'P', bold: true },
              { label: 'Stock Clearance Scheme', hot: 'S' },
              '-',
              { label: 'Auto Load : New Launch Items', hot: 'N' },
              { label: 'Auto Load : Stock Clearance', hot: 'C' },
              '-',
              { label: 'Suggested Items', hot: 'g' },
            ],
          },
          { label: 'Credit Limits Management', hot: 'L', action: 'report.credit_limits' },
          { label: 'Point Settings', hot: 'o' },
          { label: "My Own Sales Scheme's", hot: 'y' },
          { label: 'Sales Scheme on Purchase Disc%', hot: 'u', bold: true },
          { label: 'Set of Items (Bundle)', hot: 'e' },
          { label: 'Mode of Payment', hot: 'M', action: 'ledger.list:Cash-in-hand,Bank Accounts' },
          '-',
          { label: 'Restricted Items', hot: 'R', bold: true },
        ],
      },
      {
        label: 'Refresh Balances', hot: 'f', children: [
          { label: 'Single Item', hot: 'S', action: 'refresh' },
          { label: 'All Item', hot: 'A', action: 'refresh' },
          '-',
          { label: 'Single Party', hot: 'i', action: 'refresh' },
          { label: 'All Party', hot: 'l', action: 'refresh' },
        ],
      },
      {
        label: 'Ease of GST', hot: 'E', children: [
          { label: 'Item GST%/HSN/SAC/RATES Updation', hot: 'I', action: 'item.prices' },
          { label: 'Party GST No./State Updation', hot: 'P', action: 'ledger.gst' },
          { label: 'Reverse Charge on Expences', hot: 'R' },
          '-',
          { label: 'Import HSN/SAC/GST%/RATE from Excel', hot: 'm' },
          { label: 'Import ITEM/PARTY Master', hot: 'T' },
          '-',
          { label: 'GST Implementation Videos', hot: 'G', bold: true },
          { label: 'HSN/SAC Download', hot: 'H' },
          { label: 'Govt. GST Law & Rules', hot: 'o' },
        ],
      },
      {
        label: 'Opening Balances', hot: 'O', children: [
          { label: 'Ledger Openings', hot: 'L', action: 'ledger.list' },
          { label: 'Stock Openings', hot: 'S', action: 'item.list' },
        ],
      },
      { label: 'Master Statistics', hot: 'S', action: 'master.stats' },
      {
        label: 'General Reminders', hot: 'G', children: [
          { label: 'Bill Master', hot: 'B' },
          { label: 'Reminders', hot: 'R' },
          { label: 'Search', hot: 'S' },
          { label: 'Report', hot: 'p' },
        ],
      },
      {
        label: 'Shortage Report', hot: 'h', children: [
          { label: 'Whole Shortage', hot: 'W', action: 'report.reorder' },
          { label: 'Supplier wise', hot: 'S', action: 'report.reorder' },
          { label: 'Company Wise', hot: 'C', action: 'report.reorder' },
        ],
      },
      {
        label: 'Supplier vs Company', hot: 'u', children: [
          { label: 'Supplier wise Company', hot: 'S' },
          { label: 'Party wise List', hot: 'P' },
          { label: 'Company wise List', hot: 'C' },
        ],
      },
      {
        label: 'Temporary Limits', hot: 'T', children: [
          { label: 'New Temp.Entry', hot: 'N' },
          { label: 'Used Temp.Report', hot: 'U' },
        ],
      },
      '-',
      { label: 'eBusiness Setup', bold: true },
      { label: 'Flow Ledger Mart' },
      { label: 'Cloud Backup', hot: 'C', action: 'backup' },
      '-',
      {
        label: 'Survey & Offers', bold: true, children: [
          { label: 'User Satisfaction Survey', hot: 'U' },
          { label: 'ERP to ERP Ordering', hot: 'E', bold: true },
          { label: '30 Days Free AMC', hot: '3', bold: true },
          { label: 'Salesman Offer', hot: 'S' },
          { label: 'FLOW PAY - Digital Payment', hot: 'M' },
          { label: 'Connected Banking', hot: 'B' },
          { label: 'CA Recommendation', hot: 'C' },
          { label: 'PharmaNXT - Helpline', hot: 'P' },
          { label: 'FLOW LEDGER MART Registration', hot: 'R' },
          { label: 'Stock & Sales MR Panel', hot: 'k' },
          { label: 'Flow Ledger Wallet', hot: 'W' },
        ],
      },
      '-',
      {
        // Operator's opens Users (F6 there = Roles & Permissions).
        label: 'Passwords & Powers', hot: 'P', children: [
          { label: "Operator's", hot: 'O', bold: true, action: 'user.manage' },
          '-',
          { label: 'Freez Data', hot: 'F' },
          { label: "Operator's Billing Timings", hot: 'B' },
          '-',
          { label: 'Operator Login Details', hot: 'L', action: 'user.manage' },
          { label: 'Computer/Server Logins', hot: 'C' },
          '-',
          { label: 'Save Computer Passwords', hot: 'S', bold: true },
          { label: 'Change Software Password', hot: 'h', action: 'user.password' },
        ],
      },
      {
        label: 'Flow Ledger Licencing', hot: 'n', children: [
          { label: 'License Verification', hot: 'L' },
          { label: 'RENEW ONLINE', hot: 'R' },
          '-',
          { label: 'Download License', hot: 'D' },
          { label: 'Exception Code', hot: 'E' },
          '-',
          { label: 'Computer Registration', hot: 'C' },
          { label: 'License Registration', hot: 'i' },
          { label: 'Emergency Extension', hot: 'm' },
          '-',
          { label: 'SUPER PACK OTP', hot: 'S' },
          { label: 'Service System Verification', hot: 'V' },
        ],
      },
      {
        label: 'Flow Ledger Setups', hot: 'M', children: [
          { label: 'CONTROL ROOM >>>', hot: 'C', action: 'company.edit' },
          { label: 'SELF FORMAT >>>', hot: 'S' },
          { label: 'Invoice Terms & Conditions', hot: 'I' },
          { label: 'Bank Details', hot: 'B' },
          '-',
          {
            label: 'Advance Features', hot: 'A', children: [
              { label: 'Self Fields-Bill Item', hot: 'I' },
              { label: 'Self Fields-Bill Start', hot: 'S' },
              { label: 'Self Fields-Bill End', hot: 'E' },
              { label: 'Self Fields-Item Master', hot: 'M' },
              { label: 'Self Fields-Party Master', hot: 'P' },
              '-',
              { label: 'Report Designer [DMP]', hot: 'D' },
              { label: 'Report Designer [GUI]', hot: 'G' },
              '-',
              {
                label: 'Self Programming Hooks', hot: 'H', children: [
                  { label: 'Development Hook for HEADER START', disabled: true },
                  { label: 'Development Hook for HEADER END', disabled: true },
                  { label: 'Development Hook for ITEM START', disabled: true },
                  { label: 'Development Hook for ITEM END', disabled: true },
                  { label: 'Development Hook for FOOTER START', disabled: true },
                  { label: 'Development Hook for FOOTER END', disabled: true },
                  '-',
                  { label: 'Development Hook for SERVER.EXE', disabled: true },
                  { label: 'Development Hook for GST RETURNS', disabled: true },
                  '-',
                  { label: 'Import/Expot Hooks', hot: 'I' },
                ],
              },
              { label: 'Self Designed Reports', hot: 'R' },
              { label: 'SQL Query Executor', hot: 'Q' },
              '-',
              { label: 'Import Purchase', hot: 'u' },
              { label: 'Import Myorder', hot: 'y' },
              { label: 'Export Format', hot: 'x' },
              '-',
              { label: 'ERP Bridger-Export', hot: 'B' },
              { label: 'ERP Bridger-Import-Inventory', hot: 'n' },
              { label: 'ERP Bridger-Import-Accounts', hot: 'A' },
              '-',
              { label: 'Copy Protection', hot: 'C' },
            ],
          },
          '-',
          { label: 'Printer Setup', hot: 'P', action: 'print' },
          { label: 'Shortcut Buttons', hot: 'u', action: 'shortcuts' },
          {
            label: 'My Menu', hot: 'M', children: [
              { label: 'Add My Menu', hot: 'A' },
              { label: 'Modify My Menu', hot: 'M' },
            ],
          },
          {
            label: 'Activations', hot: 't', children: [
              { label: 'FLOW LEDGER GENIE App', hot: 'G' },
              '-',
              { label: 'Dynamic QRCODE Machine Settings', hot: 'D', bold: true },
              { label: 'Owner QRCODE Payment Notification', hot: 'O' },
              '-',
              { label: 'Activate Customer Display', hot: 'A' },
              { label: 'Weighing Scale Settings', hot: 'W' },
              { label: 'Virtual Keyboard', hot: 'V' },
              { label: 'Load Thyrocare Menu', hot: 'L' },
              { label: 'Digital Signature Setings', hot: 'S' },
            ],
          },
          '-',
          { label: 'Narration & Sound', hot: 'N' },
          {
            label: 'Self Defined Shortcuts', hot: 'e', children: [
              { label: 'Accounting Shortcuts', hot: 'A', action: 'shortcuts' },
              { label: 'Inventory Shortcuts', hot: 'I', action: 'shortcuts' },
            ],
          },
          { label: 'TDS Details', hot: 'T' },
          { label: 'Excise Details', hot: 'x' },
          {
            label: 'May I Help You ?', hot: 'y', children: [
              { label: 'New Upgradations', hot: 'U' },
              { label: 'New Control Room Setups', hot: 'C' },
              { label: 'New Format Commands', hot: 'F' },
              { label: 'Best Screen Resolutions', hot: 'B' },
              { label: 'FTP Uplod/Download Check', hot: 'T' },
              { label: 'Internet Connection Check', hot: 'I' },
              { label: 'Check Folder Size', hot: 'k' },
              { label: 'Unsubscribe Promotional SMS,IVR & Emails', hot: 's' },
              '-',
              { label: 'Show me Errors ?', hot: 'S' },
            ],
          },
        ],
      },
    ],
  },

  // ====================================================================== TRANSACTIONS (Alt+T)
  {
    label: 'Transactions', hot: 'T', children: [
      {
        label: 'Sale', hot: 'S', children: [
          { label: 'Bill', hot: 'B', shortcut: 'ALT+N', action: 'bill.new:SALE' },
          { label: 'Challan', hot: 'C', shortcut: 'ALT+C', action: 'bill.new:SALE_CHALLAN' },
          { label: 'Counter Sale', hot: 'u', shortcut: 'ALT+A', action: 'bill.new:COUNTER_SALE' },
          '-',
          { label: 'Modify Bill', hot: 'M', shortcut: 'ALT+M', action: 'bill.list:SALE' },
          { label: 'Modify Challan', hot: 'd', action: 'bill.list:SALE_CHALLAN' },
          { label: 'Party Transaction', hot: 'P', action: 'party.txn' },
          '-',
          { label: 'Promised Order', hot: 'O', bold: true, isNew: true },
          {
            label: 'Hold Items on Billing', hot: 'H', children: [
              { label: 'Hold Issue Pending', hot: 'P' },
              { label: 'Hold Issue Release', hot: 'R' },
              '-',
              { label: 'Hold Receive Pending', hot: 'e' },
              { label: 'Hold Receive Release', hot: 'l' },
            ],
          },
          '-',
          { label: 'Loss on Bills', hot: 'L', action: 'report.gp' },
          { label: 'Upload Server Pendings', hot: 'U' },
          { label: "Live Credit Limit's", hot: 'i', bold: true, action: 'report.credit_limits' },
          '-',
          { label: 'Customer on Retailio', hot: 't' },
          { label: 'Flow Ledger Purchaser', hot: 'G' },
          '-',
          { label: 'Missed Opportunities', hot: 's' },
        ],
      },
      {
        label: 'Purchase', hot: 'P', children: [
          { label: 'Bill', hot: 'B', shortcut: 'ALT+P', action: 'bill.new:PURCHASE' },
          { label: 'Challan', hot: 'C', action: 'bill.new:PURCHASE_CHALLAN' },
          '-',
          { label: 'Modify Bill', hot: 'M', action: 'bill.list:PURCHASE' },
          { label: 'Modify Challan', hot: 'd', action: 'bill.list:PURCHASE_CHALLAN' },
          '-',
          { label: 'Temporary Purchase', hot: 'T' },
          { label: 'Inserted Stock', hot: 'I' },
        ],
      },
      {
        label: 'Sale Return', hot: 'R', children: [
          { label: 'Credit Note', hot: 'C', action: 'bill.new:SALE_RETURN' },
          { label: 'Challan', hot: 'h' },
          '-',
          { label: 'Modify Cr.Note', hot: 'M', action: 'bill.list:SALE_RETURN' },
          { label: 'Modify Challan', hot: 'd' },
        ],
      },
      {
        label: 'Purchase Return', hot: 'u', children: [
          { label: 'Debit Note', hot: 'D', action: 'bill.new:PURCHASE_RETURN' },
          { label: 'Challan', hot: 'C' },
          { label: 'Quick Issue', hot: 'Q', bold: true, children: quickIssueMenu() },
          '-',
          { label: 'Modify Dr.Note', hot: 'M', action: 'bill.list:PURCHASE_RETURN' },
          { label: 'Modify Challan', hot: 'h' },
        ],
      },
      {
        label: 'Brk/Exp Receive', hot: 'k', children: [
          { label: 'Receive', hot: 'R', action: 'bill.new:SALE_RETURN' },
          { label: 'Challan', hot: 'C' },
          '-',
          { label: 'Modify Receive', hot: 'M', action: 'bill.list:SALE_RETURN' },
          { label: 'Modify Challan', hot: 'h' },
        ],
      },
      {
        label: 'Brk/Exp Issue', hot: 'x', children: [
          { label: 'Issue', hot: 'I', action: 'bill.new:PURCHASE_RETURN' },
          { label: 'Challan', hot: 'C' },
          { label: 'Quick Issue', hot: 'Q', bold: true, children: quickIssueMenu() },
          '-',
          { label: 'Modify Issue', hot: 'M', action: 'bill.list:PURCHASE_RETURN' },
          { label: 'Modify Challan', hot: 'h' },
        ],
      },
      '-',
      {
        label: 'GST Inward (Expenses)', hot: 'G', children: [
          { label: 'Inward', hot: 'I', action: 'voucher.new:PAYMENT' },
          { label: 'Modify', hot: 'M', action: 'voucher.list:PAYMENT' },
        ],
      },
      {
        label: 'GST Outward (Services)', hot: 'O', children: [
          { label: 'Outward', hot: 'O', action: 'voucher.new:RECEIPT' },
          { label: 'Modify', hot: 'M', action: 'voucher.list:RECEIPT' },
        ],
      },
      '-',
      {
        label: 'Replacement Issue', hot: 'l', children: [
          { label: 'Issue', hot: 'I' },
          { label: 'Challan', hot: 'C' },
          '-',
          { label: 'Modify Issue', hot: 'M' },
          { label: 'Modify Challan', hot: 'h' },
        ],
      },
      {
        label: 'Replacement Receive', hot: 'c', children: [
          { label: 'Receive', hot: 'R' },
          { label: 'Challan', hot: 'C' },
          '-',
          { label: 'Modify Receive', hot: 'M' },
          { label: 'Modify Challan', hot: 'h' },
        ],
      },
      { label: 'Stock Issue', hot: 'I', children: newModify() },
      { label: 'Stock Receive', hot: 'e', children: newModify() },
      {
        label: 'Price Diff. Sale', hot: 'D', children: [
          { label: 'Credit Note', hot: 'C', action: 'voucher.new:CREDIT_NOTE' },
          { label: 'Debit Note', hot: 'D', action: 'voucher.new:DEBIT_NOTE' },
          { label: 'Commission D/N', hot: 'o' },
          '-',
          { label: 'Modify Credit Note', hot: 'M', action: 'voucher.list:CREDIT_NOTE' },
          { label: 'Modify Debit Note', hot: 'f', action: 'voucher.list:DEBIT_NOTE' },
          { label: 'Modify Commission D/N', hot: 'y' },
        ],
      },
      {
        label: 'Price Diff. Purchase', hot: 'f', children: [
          { label: 'Credit Note', hot: 'C', action: 'voucher.new:CREDIT_NOTE' },
          { label: 'Debit Note', hot: 'D', action: 'voucher.new:DEBIT_NOTE' },
          '-',
          { label: 'Modify Credit Note', hot: 'M', action: 'voucher.list:CREDIT_NOTE' },
          { label: 'Modify Debit Note', hot: 'f', action: 'voucher.list:DEBIT_NOTE' },
        ],
      },
      {
        label: 'Conversion', hot: 'v', children: [
          { label: 'One to Many-New', hot: 'O' },
          { label: 'One to Many-Modify', hot: 'n' },
          '-',
          { label: 'Many to One-New', hot: 'M' },
          { label: 'Many to One-Modify', hot: 'a' },
        ],
      },
      { label: 'Quotation', hot: 'Q', children: newModify() },
      '-',
      { label: 'Sales Order', hot: 'a', children: orderMenu(false) },
      { label: 'Purchase Order', hot: 'h', children: orderMenu(true) },
      '-',
      {
        label: 'Pendings', hot: 'n', children: [
          { label: 'Approvals', hot: 'A' },
          { label: 'Approvals Modification', hot: 'M' },
        ],
      },
      {
        label: 'Delivery Management', bold: true, isNew: true, children: [
          { label: 'Delivery', hot: 'D' },
          { label: 'Report', hot: 'R' },
        ],
      },
      {
        label: 'Dispatch Management', hot: 'm', children: [
          { label: 'Dispatch Management', hot: 'D' },
          { label: 'TV Display-Bill Scroll', hot: 'T' },
          '-',
          { label: 'Mobile Picking Setup', hot: 'M' },
          { label: 'Picker/Packer Name', hot: 'P' },
          '-',
          { label: 'Dispatch Summary', hot: 'S', action: 'report.book:SALE' },
          { label: 'Pending Bill Status', hot: 'B' },
          '-',
          { label: 'Purchase Verification', hot: 'V' },
        ],
      },
      {
        label: 'ERP Bridger', hot: 'B', children: [
          { label: 'Export', hot: 'E' },
          '-',
          { label: 'Import-Inventory', hot: 'I' },
          { label: 'Import-Accounts', hot: 'A' },
        ],
      },
      {
        label: 'Transaction Import', hot: 'T', children: [
          { label: 'Flow Ledger Bill Import', hot: 'M' },
          '-',
          { label: 'STANDARD Bill Import', hot: 'S' },
          { label: 'GST STANDARD Bill Import', hot: 'G' },
          { label: 'XLS/DBF/CSV/PDF Bill Import', hot: 'X' },
        ],
      },
      '-',
      { label: 'Manual Upload on Server', bold: true },
    ],
  },

  // ====================================================================== ACCOUNTS
  {
    label: 'Accounts', hot: 'A', children: [
      {
        label: 'Voucher Entry', hot: 'V', shortcut: 'ALT+V', children: [
          {
            label: 'Receipt', hot: 'R', children: [
              { label: 'Receipt', hot: 'R', action: 'voucher.new:RECEIPT' },
              '-',
              { label: 'GST Advance', hot: 'A' },
              { label: 'GST Refund', hot: 'F' },
            ],
          },
          {
            label: 'Payment', hot: 'P', children: [
              { label: 'Payment', hot: 'P', action: 'voucher.new:PAYMENT' },
              '-',
              { label: 'Connected Banking', hot: 'C' },
              { label: 'GST Payment', hot: 'G' },
            ],
          },
          {
            label: 'Debit Note', hot: 'D', children: [
              { label: 'Debit Note', hot: 'D', action: 'voucher.new:DEBIT_NOTE' },
              '-',
              { label: 'D/Note Sale', hot: 'S' },
              { label: 'D/Note Purchase', hot: 'P' },
              { label: 'D/Note Expenses', hot: 'E' },
            ],
          },
          {
            label: 'Credit Note', hot: 'C', children: [
              { label: 'Credit Note', hot: 'C', action: 'voucher.new:CREDIT_NOTE' },
              '-',
              { label: 'C/Note Sale', hot: 'S' },
              { label: 'C/Note Purchase', hot: 'P' },
              { label: 'C/Note Expenses', hot: 'E' },
            ],
          },
          { label: 'Contra', hot: 'o', action: 'voucher.new:CONTRA' },
          {
            label: 'Journal', hot: 'J', children: [
              { label: 'Journal', hot: 'J', action: 'voucher.new:JOURNAL' },
              '-',
              { label: 'GST Inward (Expenses)', hot: 'I', action: 'voucher.new:PAYMENT' },
              { label: 'GST Outward (Services)', hot: 'O', action: 'voucher.new:RECEIPT' },
              { label: 'GST Reverse Charge', hot: 'R' },
              { label: 'GST Reversal', hot: 'v' },
              '-',
              { label: 'Interest Journal', hot: 'n' },
            ],
          },
          { label: 'Sale', hot: 'S', action: 'bill.new:SALE' },
          { label: 'Purchase', hot: 'u', action: 'bill.new:PURCHASE' },
        ],
      },
      '-',
      {
        label: 'Single Entry', hot: 'i', shortcut: 'ALT+I', children: [
          { label: 'Cash & Bank', hot: 'C', action: 'voucher.new:RECEIPT' },
          { label: 'Other Cash', hot: 'O' },
        ],
      },
      { label: 'Cheques/Cash', hot: 'q', shortcut: 'ALT+U', action: 'report.cashbank' },
      { label: 'P.D.Cheques/Cash', hot: 'e', shortcut: 'ALT+Q', action: 'voucher.list:RECEIPT' },
      '-',
      { label: 'Bill Taging', hot: 'B', bold: true, action: 'report.outstanding:receivable' },
      { label: 'Bill Signed/Undelivered', hot: 'g' },
      '-',
      { label: 'P.D.Cheques Payment', hot: 'y', action: 'voucher.list:PAYMENT' },
      {
        label: 'Cheque Dishonored', hot: 'D', children: [
          { label: 'Cheque Dishonored', hot: 'C' },
          { label: 'Bank Charges in Next Bill', hot: 'B' },
          { label: 'Cheque Dishonored Report', hot: 'R' },
          '-',
          { label: 'Other Charges in Next Bill', hot: 'O' },
          '-',
          { label: 'Uncleared Cash', hot: 'U' },
          '-',
          { label: 'Advance Cheques-Suppliers', hot: 'S' },
          { label: 'Advance Cheques-Customers', hot: 'm' },
        ],
      },
      {
        label: 'Bank-Pay-In-Slip', hot: 'k', children: [
          { label: 'Pay-in-Slip', hot: 'P', bold: true },
          { label: 'Pay-in-Slip (Posted)', hot: 'o' },
          { label: 'Consolidated Cheques', hot: 'C' },
          { label: 'Date Wise Cheques', hot: 'D' },
          { label: 'Single Party Cheques', hot: 'S' },
          { label: 'All Party Cheques', hot: 'A' },
          '-',
          { label: 'Cheques Clearance', hot: 'h', bold: true },
          { label: 'Consolidated Cheques', hot: 'C' },
          { label: 'Date Wise Cheques', hot: 'D' },
          { label: 'Single Party Cheques', hot: 'S' },
          { label: 'All Party Cheques', hot: 'A' },
          '-',
          { label: 'Advance Cheques - Suppliers', hot: 'u' },
          { label: 'Advance Cheques - Customers', hot: 'm' },
          '-',
          { label: 'Fund Plannings', hot: 'F' },
          { label: 'Bank Plannings', hot: 'B' },
        ],
      },
      {
        label: 'Shift in Shortage', hot: 'S', children: [
          { label: 'Cash & Bank', hot: 'C' },
          { label: 'Other Cash', hot: 'O' },
        ],
      },
      {
        label: 'Frequent Vouchers', hot: 'F', children: [
          { label: 'On Amount', hot: 'A' },
          { label: 'On Sale', hot: 'S' },
          { label: 'On Purchase', hot: 'P' },
          { label: 'Add & Less', hot: 'L' },
        ],
      },
      '-',
      { label: 'HRXpert-Payroll', hot: 'H', bold: true },
      {
        label: 'Payroll Slips', hot: 'P', children: [
          { label: 'Employee Master', hot: 'E' },
          { label: 'Holiday Master', hot: 'H' },
          { label: 'Attandance Sheet & Salary', hot: 'A' },
        ],
      },
    ],
  },

  // ====================================================================== DIGITAL
  {
    label: 'Digital', hot: 'i', children: [
      {
        label: 'Flow Ledger Wallet', icon: '👛', bold: true, children: [
          { label: 'Customer Messages' },
          '-',
          { label: 'Schedule O/s Reminder' },
          { label: 'Party Message Setup' },
          '-',
          { label: 'Wallet Usage Report' },
          { label: 'Activate Services' },
          { label: 'Wallet Recharge' },
          '-',
          { label: 'Wallet Registration' },
          { label: 'Wallet De-Activate' },
          '-',
          { label: 'Refresh Data' },
          { label: 'Tutorial Video' },
        ],
      },
      {
        label: 'Retailio Services', icon: '🔰', children: [
          { label: 'Offers:-', disabled: true },
          { label: 'Thyrocare for Staff' },
          '-',
          { label: 'Unsecured Loan' },
        ],
      },
      { label: 'ECOD Secure', icon: '🛡' },
      { label: 'Flow Ledger Mart', icon: '🛒' },
      { label: 'Find Customer Info', isNew: true },
      {
        label: 'Flow Pay', icon: '₹', children: [
          { label: 'Pending Posting' },
          { label: 'Pending Settlement' },
          { label: 'Transaction Enquiry' },
          '-',
          { label: 'Dashboard' },
          { label: 'Collection Report' },
          '-',
          { label: 'Update Server' },
          { label: 'Register Complaint' },
          '-',
          { label: 'FLOW PAY ID' },
          { label: 'Schedule Customers' },
        ],
      },
      '-',
      {
        label: 'Connected Banking', children: [
          { label: 'Payment' },
          '-',
          { label: 'Cancel Payment' },
          { label: 'Scheduled Payments' },
          { label: 'Payment History' },
          '-',
          { label: 'MISC Services' },
        ],
      },
      { label: 'Bank Reconciliation' },
      { label: 'Online Bank Statement' },
      '-',
      {
        label: 'Registration', children: [
          { label: 'Connected Banking' },
          { label: 'Flow Pay Registration' },
        ],
      },
      {
        label: "Video's", children: [
          { label: 'Flow Pay Implementation' },
          { label: 'Connected Banking' },
          '-',
          { label: 'Digital Order & Payment' },
        ],
      },
    ],
  },

  // ====================================================================== BOOKS (Alt+B)
  {
    label: 'Books', hot: 'B', children: [
      { label: 'Cash & Bank Book', hot: 'C', action: 'report.cashbank' },
      { label: 'All Ledgers', hot: 'A', shortcut: 'ALT+L', action: 'report.ledger' },
      {
        label: 'Entry Books', hot: 'E', children: [
          { label: 'Day Book', hot: 'D', action: 'report.daybook' },
          { label: 'Selected Book', hot: 'S' },
          { label: 'Journal Book', hot: 'J', action: 'voucher.list:JOURNAL' },
          { label: 'Debit Note', hot: 'b', action: 'voucher.list:DEBIT_NOTE' },
          { label: 'Credit Note', hot: 't', action: 'voucher.list:CREDIT_NOTE' },
          { label: 'Receipt Book', hot: 'R', action: 'voucher.list:RECEIPT' },
          { label: 'Payment Book', hot: 'P', action: 'voucher.list:PAYMENT' },
          { label: 'Contra Book', hot: 'C', action: 'voucher.list:CONTRA' },
          { label: 'Sale Book', hot: 'a', action: 'report.salebook' },
          { label: 'Purchase Book', hot: 'u', action: 'report.book:PURCHASE' },
          '-',
          { label: 'Ledger Day Book', hot: 'L' },
          { label: 'Item Day Book', hot: 'I' },
        ],
      },
      { label: 'Debtors Ledgers', hot: 'D', action: 'ledger.list:Sundry Debtors' },
      { label: 'Creditors Ledgers', hot: 'r', action: 'ledger.list:Sundry Creditors' },
      '-',
      { label: 'Purchase Register', hot: 'P', action: 'report.book:PURCHASE' },
      { label: 'Sale Register', hot: 'S', action: 'report.book:SALE,COUNTER_SALE' },
      { label: 'Duties & Taxes', hot: 'T', action: 'ledger.list:Duties & Taxes' },
      {
        label: 'Analytical Summaries', hot: 'n', bold: true, children: [
          { label: 'Ledger Summary', hot: 'L', action: 'report.group' },
          { label: 'Account Group Summary', hot: 'A', action: 'report.group' },
          '-',
          { label: 'Party wise Sales', hot: 'P', action: 'report.party_sale' },
          { label: 'Company wise Sales', hot: 'C', action: 'report.company_sale' },
          { label: 'Item wise Sales', hot: 'I', action: 'report.itemsale' },
          '-',
          { label: 'Operator wise Sales', hot: 'O' },
          { label: 'Terminal wise Sales', hot: 'T' },
          '-',
          { label: 'Sales & Purchase Summary', hot: 'S', action: 'report.mis' },
          { label: 'Payment & Receipts Summary', hot: 'y' },
          { label: 'Cash & Bank Summary', hot: 'B', action: 'report.cashbank' },
        ],
      },
      '-',
      {
        label: 'Outstandings', hot: 'O', shortcut: 'ALT+O', children: [
          { label: 'Whole', hot: 'W', action: 'report.outstanding:receivable' },
          { label: 'Party Wise', hot: 'P', action: 'report.outstanding:receivable' },
          { label: 'M.R. Wise', hot: 'M' },
          { label: 'Route Wise', hot: 'R' },
          { label: 'Area Wise', hot: 'A' },
          { label: 'Delivery Wise', hot: 'D' },
          { label: 'Ageing', hot: 'g', bold: true },
          '-',
          { label: 'Bill Taging/Selective O/s', hot: 'B', action: 'report.outstanding:receivable' },
          { label: 'Un-Taged Bills', hot: 'U' },
          '-',
          { label: 'Creditors: Whole', hot: 'C', action: 'report.outstanding:payable' },
          { label: 'Creditors: Party Wise', hot: 'y', action: 'report.outstanding:payable' },
          { label: 'Creditors: Ageing', hot: 'n', bold: true },
          '-',
          { label: 'Pending C/N, D/N, Adv...', hot: 'e' },
        ],
      },
      {
        label: 'Collection Reports', hot: 'l', children: [
          { label: 'Collection Entry Summary', hot: 'S' },
          { label: 'Collection Entry Report', hot: 'R' },
          '-',
          { label: 'Party Collection', hot: 'P' },
          { label: 'M.R. Collection', hot: 'M' },
          { label: 'Route Collection', hot: 'o' },
          { label: 'Area Collection', hot: 'A' },
          { label: 'Delivery Collection', hot: 'D' },
          '-',
          { label: 'M.R. Bill Wise Collection', hot: 'B' },
          { label: 'Route Bill Wise Collection', hot: 'u' },
          { label: 'Area Bill Wise Collection', hot: 'e' },
          { label: 'Delivery Bill Wise Collection', hot: 'l' },
          '-',
          {
            label: 'Bill Status Party/Area', hot: 't', children: [
              { label: 'All Bill', hot: 'A' },
              { label: 'Single Party', hot: 'P' },
              { label: 'Single M.R.', hot: 'M' },
              { label: 'Single Route', hot: 'R' },
              { label: 'Single Area', hot: 'e' },
            ],
          },
          { label: 'Bill Status Single', hot: 'i' },
        ],
      },
      {
        label: 'Interest Reports', hot: 'I', children: [
          { label: 'Ledger Interest', hot: 'L' },
          { label: 'Mahajani Interest', hot: 'h' },
          { label: 'Bill Wise Interest', hot: 'B' },
          '-',
          { label: 'M.R. Wise Interest', hot: 'M' },
          { label: 'Route Wise Interest', hot: 'R' },
          { label: 'Area Wise Interest', hot: 'A' },
          { label: 'Delivery Wise Interest', hot: 'D' },
          { label: 'A/c Group Interest', hot: 'G' },
          { label: 'Debtors Interest Summary', hot: 'e' },
          { label: 'Creditors Interest Summary', hot: 'C' },
        ],
      },
      { label: 'Depreciation', hot: 'p' },
      '-',
      {
        label: 'T-Format Printing', hot: 'F', children: [
          { label: 'Cash & Bank Book', hot: 'C', action: 'report.cashbank' },
          { label: 'Day/Sale/Purchase', hot: 'D', action: 'report.daybook' },
          { label: 'T-Format Any Ledger', hot: 'T', action: 'report.ledger' },
          { label: 'Cash+Bank <132 Column>', hot: 'B' },
          { label: 'Cash+Party <132 Column>', hot: 'P' },
        ],
      },
      {
        label: 'Multi Printing', hot: 'M', children: [
          { label: 'Multi Ledger Printing', hot: 'L' },
          { label: 'Multi Voucher Printing', hot: 'V' },
          { label: 'Trial Balance Printing', hot: 'T', action: 'report.tb' },
          { label: 'Interest & TDS', hot: 'I' },
        ],
      },
      { label: 'Bank Reconciliation', hot: 'k' },
      '-',
      { label: 'Cheque Management', hot: 'h' },
      {
        label: 'Claims & Incentives', hot: 'i', children: [
          { label: 'Purchase Claims Pendings', hot: 'P' },
          { label: 'Purchase Claims Closed', hot: 'u' },
          '-',
          { label: 'Sales Claims Pendings', hot: 'S' },
          { label: 'Sales Claims Closed', hot: 'a' },
          '-',
          { label: 'Collection Incentives', hot: 'C' },
        ],
      },
      {
        label: 'Budget Analysis', hot: 'u', children: [
          { label: 'Ledger', hot: 'L' },
          { label: 'Groups', hot: 'G' },
        ],
      },
      { label: 'Target Analysis', hot: 'g' },
      '-',
      { label: 'General Reminders', hot: 'G' },
    ],
  },

  // ====================================================================== FINAL REPORTS
  {
    label: 'Final Reports', hot: 'F', children: [
      { label: 'Trial Balance', hot: 'T', action: 'report.tb' },
      { label: 'Profit & Loss', hot: 'P', action: 'report.pl' },
      { label: 'Balance Sheet', hot: 'B', action: 'report.bs' },
      { label: 'Group Summary', hot: 'G', action: 'report.group' },
      { label: 'Financial Analysis', hot: 'F' },
      { label: 'Ratio Analysis', hot: 'R' },
      {
        label: 'Cash & Fund Flow', hot: 'C', children: [
          { label: 'Cash & Bank', hot: 'C', action: 'report.cashbank' },
          '-',
          { label: 'Cash Flow', hot: 'a' },
          { label: 'Bank Flow', hot: 'B' },
          '-',
          { label: 'Fund Flow', hot: 'F' },
        ],
      },
      '-',
      { label: 'Goods Account', hot: 'o' },
      { label: 'Comparisons', hot: 'm' },
      { label: 'Ledger Analysis', hot: 'L', action: 'report.ledger' },
      {
        label: 'IT-ST-Auditor Reports', hot: 'I', children: [
          { label: 'Unchecked Voucher', hot: 'U' },
          { label: 'Search Voucher', hot: 'V' },
          '-',
          { label: 'Party Wise Sale Above', hot: 'P' },
          { label: 'Supplier Wise Purchase Above', hot: 'l' },
          { label: 'Sale Above', hot: 'S' },
          { label: 'Sale W/o GST', hot: 'G' },
          { label: 'Sale W/o DL Number', hot: 'D' },
          { label: 'Purchase Above', hot: 'h' },
          { label: 'Cash Transaction Above', hot: 'T' },
          { label: 'Party Cash Receipt', hot: 'R' },
          { label: 'Supplier Cash Payment', hot: 'y' },
          '-',
          { label: 'Sundry Debtors Above', hot: 'b' },
          { label: 'Sundry Creditors Above', hot: 'C' },
          { label: 'Capital Accounts', hot: 'A' },
          { label: 'Stock Summaries', hot: 'k', action: 'report.stock' },
          '-',
          { label: 'Convert Wrong Debtors/Creditors', hot: 'W' },
        ],
      },
    ],
  },

  // ====================================================================== GST (Alt+G)
  {
    label: 'Gst', hot: 'G', children: [
      { label: 'GST Version 29-Jun-2026', disabled: true },
      '-',
      { label: 'Generate eInvoice', hot: 'e' },
      { label: 'GSTR 1 (Outward Supply)', hot: '1', action: 'report.book:SALE,COUNTER_SALE' },
      { label: 'GSTR 2 (Inward Supply)', hot: '2', action: 'report.book:PURCHASE' },
      { label: 'GSTR 2A-2B Reconciliation', hot: 'A' },
      {
        label: 'Internal Audit', hot: 'I', children: [
          { label: 'GST Return Errors', hot: 'G', bold: true },
          { label: 'Sale/Purchase Mismatch', hot: 'S' },
          { label: 'Entries Not in Return', hot: 'E' },
          '-',
          { label: 'Missing/Cancelled/Duplicate', hot: 'M' },
          { label: 'Tax Mismatch', hot: 'T' },
          { label: 'Missing Receipt No.', hot: 'R' },
          { label: 'Pending Challan (Loaded)', hot: 'P' },
          { label: 'Loaded Challan Bill Not Found', hot: 'L' },
        ],
      },
      '-',
      { label: 'GSTR 3B', hot: '3', action: 'report.gstr3b' },
      { label: 'GSTR 3B Offset Report', hot: 'O' },
      { label: 'GSTR 9', hot: '9' },
      { label: 'GST Tax Rates', hot: 'T', action: 'item.list' },
      { label: 'Export Invoice Print', hot: 'x' },
      '-',
      { label: 'Tax Clubbing', hot: 'C' },
      { label: 'Misc. GST Reports', hot: 'M' },
      { label: 'Bulk-HSN Verification', hot: 'B' },
      { label: 'GST Return Video', hot: 'V' },
      '-',
      { label: 'GSTR Settings', hot: 'S' },
      { label: 'Auditors Detail', hot: 'u' },
      { label: 'Update GST Patch', hot: 'P' },
      { label: 'Misc. GST Returns', hot: 'R' },
      { label: 'GST Register & Returns', hot: 'g', bold: true },
      '-',
      { label: 'GST RCM Statement', hot: 'N' },
      { label: 'GST Advance Statement', hot: 'd' },
      { label: 'GST Payment Statement', hot: 'y' },
      '-',
      {
        label: 'Tax Registers & Summaries', hot: 'a', children: [
          { label: 'Sales Register', hot: 'S', action: 'report.book:SALE,COUNTER_SALE' },
          { label: 'Sales Summary', hot: 'a' },
          { label: 'Purchase Register', hot: 'P', action: 'report.book:PURCHASE' },
          { label: 'Purchase Summary', hot: 'u' },
          { label: 'Sales & Purchase Summary', hot: '&', action: 'report.mis' },
          { label: 'Interstate Purchase Analysis', hot: 'I' },
          { label: 'Stock Transfer Tax Analysis', hot: 'T' },
          { label: 'CESS Re-Chacking', hot: 'C' },
          { label: 'Tax% wise Trading', hot: 'w' },
        ],
      },
      {
        label: 'TDS/TCS Reports', hot: 'D', children: [
          { label: 'Form 16A', hot: '1' },
          { label: 'Challan Entry Single', hot: 'S' },
          { label: 'Challan Entry Bulk', hot: 'B' },
          { label: 'Form 26Q', hot: '2' },
          { label: 'Form 27Q', hot: 'Q' },
          { label: 'Form 27EQ (TCS)', hot: 'E' },
          { label: 'Form 27A', hot: 'A' },
          { label: 'Form 27B', hot: 'F' },
          { label: 'Form 27C', hot: 'C' },
          { label: 'TDS/TCS Summary', hot: 'T' },
          { label: 'Monthly Statement', hot: 'M' },
          { label: 'MISC Deductor Detail', hot: 'D' },
          { label: 'Name As on Pan Card', hot: 'N' },
        ],
      },
      { label: 'Forms Iss./Receivable', hot: 'F' },
    ],
  },

  // ====================================================================== E-WAY
  {
    label: 'e-Way', hot: 'W', children: [
      { label: 'Generate Individual', hot: 'I' },
      { label: 'Generate Consolidated', hot: 'G' },
      '-',
      { label: 'Update Vehicle', hot: 'V' },
      { label: 'Update Transporter ID', hot: 'U' },
      { label: 'Re-generate Consolidated', hot: 'R' },
      { label: 'Get EWAY Bill Details', hot: 'E' },
      { label: 'Get Consolidated EWAY Details', hot: 'D' },
      { label: 'Cancel EWAY Bill', hot: 'C' },
      { label: 'Reject EWAY Bill', hot: 'j' },
      { label: 'Closure of Eway Bill', hot: 'o' },
      '-',
      { label: 'Login Details', hot: 'L' },
    ],
  },

  // ====================================================================== STOCKS (Alt+S)
  {
    label: 'Stocks', hot: 'S', children: [
      { label: 'Current Stock', hot: 'C', action: 'report.stock' },
      { label: 'Stock Analysis', hot: 'A', bold: true, action: 'report.itemsale' },
      '-',
      { label: 'Filtered Stock', hot: 'F', action: 'report.filtered_stock' },
      { label: 'Batch Stock', hot: 'B', action: 'report.batch' },
      { label: 'Dump Stock', hot: 'D' },
      { label: 'Hold/Ban Stock', hot: 'H' },
      { label: 'Stock Valuation', hot: 'V', action: 'report.stock' },
      '-',
      { label: 'Minimum Level Stock', hot: 'M', action: 'report.reorder' },
      { label: 'Maximum Level Stock', hot: 'x' },
      { label: 'Expiry Stock', hot: 'E', action: 'report.expiry' },
      { label: 'Near Expiry Stock', hot: 'N', action: 'report.near_expiry' },
      { label: 'Stock Life Statement', hot: 'L', action: 'item.register' },
      { label: 'Batch Purchase Type', hot: 'P' },
      '-',
      {
        label: 'Department Reports', hot: 'R', bold: true, children: [
          { label: 'My Item Report', hot: 'M' },
          { label: 'Restricted Items Report', hot: 'R' },
          '-',
          { label: 'TB Report', hot: 'T' },
          { label: 'Tramadol Report', hot: 'a' },
          '-',
          { label: 'NDPS Report', hot: 'N' },
          '-',
          { label: 'Narcotic Report', hot: 'c' },
          { label: 'Schedule H Report', hot: 'H' },
          { label: 'Schedule H1 Report', hot: '1' },
        ],
      },
      { label: 'Merge Reports', hot: 'g' },
    ],
  },

  // ====================================================================== DAILY REPORTS (Alt+D)
  {
    label: 'Daily Reports', hot: 'D', children: [
      {
        label: 'Daily Working', hot: 'D', children: [
          { label: 'Dispatch Summary', hot: 'D', bold: true, action: 'report.book:SALE' },
          '-',
          { label: 'Re-order Management', hot: 'R', bold: true, action: 'report.reorder' },
          { label: 'Stock & Sale Analysis', hot: 'S', bold: true, action: 'report.itemsale' },
          { label: 'Multi Bill/Other Printing', hot: 'M' },
          { label: 'CHALLAN TO BILL >>>', hot: 'C' },
          { label: "Pending Challan's", hot: 'P', action: 'bill.list:SALE_CHALLAN' },
          '-',
          { label: 'Dispatch Management Reports', hot: 'i' },
          { label: 'Rate Comparison Statement', hot: 'a' },
          '-',
          { label: 'Merge Bills in Single O/s', hot: 'g' },
          { label: 'Party Not Visited', hot: 'V' },
          { label: 'Bill Not Printed <New>', hot: 'B' },
          { label: 'Bill Not Printed <After Modify>', hot: 'N' },
        ],
      },
      {
        label: 'Fast Reports', hot: 'F', children: [
          { label: 'Schedule Email-Party', hot: 'P', bold: true },
          { label: 'Schedule Email-Company', hot: 'C' },
          '-',
          { label: 'Stock & Sales', hot: 'S', action: 'report.itemsale' },
          { label: 'Item/Party Sales Statement', hot: 'I' },
          { label: 'Item/Party Sales Summary', hot: 'u' },
          '-',
          { label: 'Party Sales Tax Statements', hot: 'T' },
          '-',
          { label: 'SMS/Mail to Debtors', hot: 'D' },
          { label: 'SMS/Mail to Creditors', hot: 'r' },
          { label: 'SMS/Mail to Recent Bill Customers', hot: 'B' },
          { label: 'SMS/Mail to My List', hot: 'M' },
          '-',
          { label: 'Re-Send Scheduled Emails', hot: 'e' },
        ],
      },
      '-',
      { label: 'Business Analysis', hot: 'B', bold: true, action: 'report.mis' },
      {
        label: 'Order CRM', hot: 'O', bold: true, children: [
          { label: 'Order Calling', hot: 'O' },
          '-',
          { label: 'Call Scheduler', hot: 'C' },
          { label: 'Transfer Calls', hot: 'T' },
          { label: 'Transfer Report', hot: 'R' },
        ],
      },
      {
        label: 'Business on Google MAP', hot: 'G', children: [
          { label: 'Date wise Sales', hot: 'D' },
          '-',
          { label: 'Company Sales', hot: 'C' },
          { label: 'Item Sales', hot: 'I' },
          { label: 'M.R. Sales', hot: 'M' },
          { label: 'Area Sales', hot: 'A' },
          { label: 'Rout Sales', hot: 'R' },
          '-',
          { label: 'Sale Increase/Decrease', hot: 'S' },
          { label: 'Active/Inactive Customers', hot: 'v' },
          '-',
          {
            label: 'Outstanding', hot: 'O', children: [
              { label: 'Whole O/s', hot: 'W', action: 'report.outstanding:receivable' },
              { label: 'M.R. O/s', hot: 'M' },
              { label: 'Area O/s', hot: 'A' },
              { label: 'Rout O/s', hot: 'R' },
            ],
          },
          '-',
          { label: 'Find New Customers', hot: 'F', bold: true },
          '-',
          { label: 'Party GEO Location', hot: 'P' },
          { label: 'Google Map Video', hot: 'G', bold: true, icon: '▶' },
        ],
      },
      '-',
      {
        label: 'Sale Report', hot: 'S', children: [
          { label: 'Mode of Payment Report', hot: 'M', action: 'report.mop' },
          { label: 'Multi Currency Report', hot: 'u' },
          '-',
          {
            label: 'Commission Calculation', hot: 'C', children: [
              { label: 'Report', hot: 'R' },
              { label: 'Setup', hot: 'S' },
            ],
          },
          {
            label: 'M.R. Sale', hot: 'R', children: [
              { label: 'M.R. Sale Statement', hot: 'S', action: 'report.mrsale:single' },
              { label: 'M.R. Summary', hot: 'u', action: 'report.mrsale:summary' },
              { label: 'M.R./ Company Wise Summary', hot: 'C' },
              { label: 'M.R./Product Wise Summary', hot: 'P', action: 'report.mrsale:items' },
            ],
          },
          {
            label: 'Party Sales', hot: 'P', children: [
              { label: 'Party Statement', hot: 'S', action: 'party.txn' },
              { label: 'Party Summary', hot: 'u' },
              { label: 'Party/Company Wise Summary', hot: 'C' },
              { label: 'Party/Product Wise Summary', hot: 'P' },
            ],
          },
          {
            label: 'Party Return Analysis', hot: 'A', children: [
              { label: 'All Parties', hot: 'A' },
              { label: 'Single Party Products', hot: 'S' },
              { label: 'All Company', hot: 'C' },
              { label: 'Debit/Credit Analysis', hot: 'D' },
              { label: 'Brk/Exp Statement', hot: 'B' },
            ],
          },
          {
            label: 'Company/Salt/Category/HSN/SAC', hot: 'y', children: [
              { label: 'Summary', hot: 'S' },
              { label: 'M.R. Summary', hot: 'M' },
              { label: 'Party Summary', hot: 'P' },
              { label: 'Product Summary', hot: 'r' },
              { label: 'Analysis With', hot: 'A' },
            ],
          },
          { label: 'Godwon Transfer Report', hot: 'G' },
          '-',
          {
            label: 'Periodic Sales Growth', hot: 'e', bold: true, children: [
              { label: 'Party', hot: 'P' },
              { label: 'M.R.', hot: 'M' },
              { label: 'Area', hot: 'A' },
              { label: 'Rout', hot: 'R' },
              { label: 'Company', hot: 'C' },
              { label: 'Item', hot: 'I', action: 'report.itemsale' },
            ],
          },
          { label: 'Day Wise Summary', hot: 'D' },
          { label: 'Month Wise Summary', hot: 'n' },
          { label: 'Bill Wise Statement', hot: 'B', action: 'report.book:SALE,COUNTER_SALE' },
          '-',
          { label: 'Staff Working Summary', hot: 'f' },
          {
            label: 'Operator Sales', hot: 'O', children: [
              { label: 'Operator Sales Summary', hot: 'O' },
              '-',
              { label: 'Incentive Report', hot: 'I' },
              { label: 'Incentive Setup', hot: 'S' },
            ],
          },
          { label: 'Terminal Sales Summary', hot: 'T' },
          { label: 'Operator/Terminal Sales Summary', hot: 'l' },
          { label: 'Operator Wise PUSHSALE', hot: 'H' },
          '-',
          { label: 'Supplier wise Sale', hot: 'w' },
          { label: 'Company/Supplier Lifting/Offtake', hot: 'L' },
          { label: 'Extra Free & Replacement', hot: 'x' },
          { label: 'Brk/Exp Adjustment', hot: 'k' },
          { label: 'Order v/s Bill', hot: 'v' },
          { label: 'Product/Company/Category wise Report', hot: 't' },
          '-',
          { label: 'Company/Category wise Tax Summary', hot: 'X' },
          { label: 'Product wise Sales', hot: 'i', action: 'report.itemsale' },
          { label: 'Product wise Purchase', hot: 'h' },
        ],
      },
      {
        label: 'Purchase Report', hot: 'P', children: [
          { label: 'Day Wise Summary', hot: 'D' },
          { label: 'Month Wise Summary', hot: 'M' },
          { label: 'Bill Wise Summary', hot: 'B', action: 'report.book:PURCHASE' },
          '-',
          { label: 'Supplier Wise Statement', hot: 'S' },
          { label: 'Bill/Item Wise Statement', hot: 'I' },
          { label: 'Supplier/Item Wise Statement', hot: 't' },
          { label: 'Supplier/ Company Wise Summary', hot: 'u' },
          '-',
          { label: 'Company Wise Summary', hot: 'C' },
          { label: 'Company/Product Wise Summary', hot: 'o' },
          { label: 'Company/Supplier Wise Summary', hot: 'm' },
          '-',
          { label: 'Shelf Life Statement', hot: 'h' },
          { label: 'Purchase Difference Statement', hot: 'P' },
          { label: 'Purchase Checking Statement', hot: 'k' },
          { label: 'Purchase Costing Comparison-MIS', hot: 'g' },
          { label: 'Purchase Discount Mismatch', hot: 'r' },
          { label: 'Product/Company/Category wise Report', hot: 'y' },
        ],
      },
      {
        label: 'Inventory Reports', hot: 'I', children: [
          { label: "Today's Sale,Purchase,Closing", hot: 'T', action: 'report.mis' },
          '-',
          { label: 'Various Types of Items List', hot: 'V', action: 'item.list' },
          { label: 'Near Expiry/Expired Report', hot: 'N', action: 'report.expiry' },
          { label: 'Dump Stock Report', hot: 'D' },
          { label: 'Hold/Ban Stock Report', hot: 'H' },
          { label: 'Negative Stock Report', hot: 'e' },
          { label: 'Rates Difference Stock List', hot: 'R' },
          { label: 'Godwon Wise Stock Status', hot: 'G', action: 'report.stock' },
          { label: 'Manufacturer/Supplier wise Stock', hot: 'M', action: 'report.stock' },
          '-',
          { label: 'Bank Statement (Stock Valuation)', hot: 'B', action: 'report.stock' },
          '-',
          { label: "Today's Gross Profit", hot: 'y', action: 'report.gp' },
          { label: 'Stock Register Printing', hot: 'S', action: 'item.register' },
          { label: 'Stock Register Salt/Category/HSN/SAC', hot: 'l' },
          '-',
          { label: 'Stock Ageing Analysis', hot: 'A' },
          { label: 'Fast & Slow Moving Items', hot: 'F' },
          { label: 'Item Wise Tax Difference', hot: 'I' },
          { label: 'Random Stock Checking', hot: 'k' },
          '-',
          {
            label: 'Barcode Printing', hot: 'P', children: [
              { label: 'Selected Item', hot: 'I' },
              { label: 'Select Batch', hot: 'B' },
              { label: 'Select Company', hot: 'C' },
            ],
          },
          { label: 'Barcode V/s Stock', hot: 'c' },
          { label: 'Order Form', hot: 'O' },
          { label: 'New Item Added/Mrp Changed', hot: 'w' },
        ],
      },
      { label: 'ABC Analysis', hot: 'A' },
      { label: 'All Accounting Records', hot: 'c', action: 'report.daybook' },
      { label: 'Purchase Planning', hot: 'l', action: 'report.reorder' },
      '-',
      { label: 'SQL Query Executor', disabled: true },
    ],
  },

  // ====================================================================== REPORTS (Alt+R)
  {
    label: 'Reports', hot: 'R', children: [
      {
        label: 'Sale Analysis', hot: 'S', children: [
          { label: 'Consolidated Sale Book', hot: 'C', action: 'report.book:SALE,COUNTER_SALE' },
          { label: 'Sale Book', hot: 'S', action: 'report.book:SALE' },
          { label: 'Sale Return Book', hot: 'R', action: 'report.book:SALE_RETURN' },
          '-',
          {
            label: 'Misc.Sale Report', hot: 'M', children: [
              { label: 'Item Wise Sale,S/R,B/E', hot: 'I', action: 'report.itemsale' },
              '-',
              { label: 'Sale Statement With Item', hot: 'S' },
              { label: 'Sale Statement W/o Item', hot: 'W' },
              '-',
              { label: 'Rate Comparison Statement', hot: 'R' },
              { label: 'Special Rate Billing', hot: 'p' },
              { label: 'Price Diff. Report', hot: 'D' },
              '-',
              { label: 'Free Issue Statement', hot: 'F' },
              '-',
              { label: 'Challan History', hot: 'H', action: 'report.book:SALE_CHALLAN' },
              { label: 'Challan Adjusted in Bill', hot: 'A' },
              { label: 'Challan Summary', hot: 'C' },
              { label: 'Bills with Adjusted Challan', hot: 'B' },
              { label: 'Sale/Challan/Coll.Analysis', hot: 'l' },
              '-',
              { label: 'Add/Less in Bill', hot: 'd' },
              { label: 'Sale Return Against Bill', hot: 't' },
            ],
          },
          {
            label: 'Item Sale Analysis', hot: 'I', bold: true, children: [
              { label: 'Party/Item Wise Sale', hot: 'P' },
              { label: 'Item/Party Wise Sale', hot: 'I', action: 'report.itemsale' },
              { label: 'Area/Party/Item Wise Sale', hot: 'A' },
              { label: 'Area Wise Party Summary', hot: 'W' },
              { label: 'Area Sale Summary', hot: 'S' },
            ],
          },
          {
            label: 'Incentives/Claims', hot: 'n', children: [
              {
                label: 'Field Staff Incentives', hot: 'F', children: [
                  { label: 'Incentive Statement', hot: 'S' },
                  '-',
                  { label: 'General Incentive', hot: 'G' },
                  { label: 'Party Incentive', hot: 'P' },
                  { label: 'Company Incentive', hot: 'C' },
                  { label: 'Item Incentive', hot: 'I' },
                ],
              },
              {
                label: 'Party Incentives', hot: 'P', children: [
                  { label: 'Incentive Statement', hot: 'S' },
                  '-',
                  { label: 'Party Incentive', hot: 'P' },
                  { label: 'Company Incentive', hot: 'C' },
                  { label: 'Item Incentive', hot: 'I' },
                  '-',
                  { label: 'Commission Statement', hot: 'm' },
                ],
              },
            ],
          },
          '-',
          {
            label: 'Free Issued Analysis', hot: 'F', children: [
              { label: 'Free Issued', hot: 'F' },
              { label: 'Extra Free Issued', hot: 'E' },
              { label: 'Free Issued Statement', hot: 'S' },
            ],
          },
          {
            label: 'S/R, Brk, Rep/###, Other', hot: '/', children: [
              { label: 'S/R,Brk,Rep & ### Bills', hot: 'S', action: 'report.book:SALE_RETURN' },
              { label: 'Monthly ### Items Report', hot: 'M' },
            ],
          },
          { label: 'Bank Statement (Stock Valuation)', hot: 'B', action: 'report.stock' },
          {
            label: 'Tpt./Order/Dispatch Document', hot: 'T', children: [
              { label: 'Dispatch/Tpt.Register', hot: 'T' },
              { label: 'Dispatch/Order Register', hot: 'O' },
              { label: 'Dispatch Documents', hot: 'D' },
            ],
          },
          {
            label: 'Sale/Amendment/Collection', hot: 'A', children: [
              { label: 'Item Sale', hot: 'I', action: 'report.itemsale' },
              { label: 'Amended', hot: 'A' },
              { label: 'Collection', hot: 'C' },
              { label: 'Collection & Sale', hot: 'S' },
              { label: 'Collection Summary', hot: 'u' },
            ],
          },
          {
            label: 'Discount Statement', hot: 'D', children: [
              { label: 'Item Wise Discount Summary', hot: 'I' },
              { label: 'Party Wise Discount Summary', hot: 'P' },
              '-',
              { label: 'Bill Wise Discount', hot: 'B' },
              { label: 'Bill Wise Discount Summary', hot: 'S' },
              '-',
              { label: 'Item Discount Analysis', hot: 't' },
              { label: 'Company Discount Analysis', hot: 'C' },
            ],
          },
          '-',
          {
            label: 'Monthly Summaries', hot: 'o', children: [
              { label: 'Party Sale Summary', hot: 'P' },
              { label: 'M.R. Sale Summary', hot: 'M' },
              { label: 'Route Sale Summary', hot: 'R' },
              { label: 'Area Sale Summary', hot: 'A' },
            ],
          },
          {
            label: 'Item Wise Sale', hot: 'W', children: [
              { label: 'Party/Month Wise', hot: 'P' },
              { label: 'M.R./Month Wise', hot: 'M' },
              { label: 'Area/Month Wise', hot: 'A' },
              { label: 'Route/Month Wise', hot: 'R' },
              { label: 'Item Wise Sale', hot: 'I', action: 'report.itemsale' },
              { label: 'Single Item Sale', hot: 'S', action: 'report.single_item_sale' },
              { label: 'Party Sale Summary', hot: 'y' },
              { label: 'M.R. Sale Summary', hot: '.' },
              { label: 'Route Sale Summary', hot: 'u' },
              { label: 'Area Sale Summary', hot: 'e' },
              { label: 'Company Sale Summary', hot: 'C', action: 'report.company_sale' },
              '-',
              { label: 'All Items/Months', hot: 'l' },
              { label: 'All Items/Parties', hot: 't' },
              { label: 'All Items/M.R.', hot: '/' },
              { label: 'All Items/Area', hot: 'r' },
              { label: 'All Items/Route', hot: 'o' },
              '-',
              { label: 'Multi Item Stock Registers', hot: 'g' },
              { label: 'Multi Item Monthly Sale & Closing Stock', hot: 'h' },
              { label: 'Multi Item Day Wise Sale', hot: 'D' },
            ],
          },
          { label: 'Company Wise Sale', hot: 'C', children: companySaleMenu('Sale') },
          {
            label: 'Party Wise Sale', hot: 'P', children: [
              { label: 'Items/Month Wise', hot: 'I' },
              { label: 'Company/Month Wise', hot: 'C' },
              '-',
              { label: 'All Parties/Month', hot: 'A' },
              { label: 'Party Wise Sale', hot: 'P', action: 'report.party_sale' },
              { label: 'Single Party Sale', hot: 'S', action: 'report.single_party_sale' },
              '-',
              { label: 'Parties/All Items', hot: 't' },
              { label: 'Party Offtake Report', hot: 'O' },
              '-',
              { label: 'Sale/Collection/O/s', hot: 'l', action: 'report.outstanding:receivable' },
              { label: 'Bill Collection Ageing', hot: 'g' },
              { label: 'Sale/Collection Analysis', hot: 'y' },
              '-',
              { label: 'Bill Wise Sale & Cost', hot: 'B', action: 'report.gp' },
              { label: 'Brk/Exp/Repl.Report', hot: 'k' },
              { label: 'Brk/Exp/Repl.Summary', hot: 'u' },
            ],
          },
          { label: 'M.R. Wise Sale', hot: 'M', children: regionSaleMenu('M.R.') },
          { label: 'Route Wise Sale', hot: 'u', children: regionSaleMenu('Route') },
          { label: 'Area Wise Sale', hot: 'e', children: regionSaleMenu('Area') },
        ],
      },
      {
        label: 'Purchase Analysis', hot: 'P', children: [
          { label: 'Consolidated Purchase Book', hot: 'C', action: 'report.book:PURCHASE,PURCHASE_CHALLAN' },
          { label: 'Purchase Book', hot: 'P', action: 'report.book:PURCHASE' },
          { label: 'Purchase Return Book', hot: 'R', action: 'report.book:PURCHASE_RETURN' },
          {
            label: 'Misc.Purchase Report', hot: 'M', children: [
              { label: 'Item Wise Purchase, P/R, B/E', hot: 'I' },
              '-',
              { label: 'Purchase Statement with Item', hot: 'P' },
              { label: 'Purchase Statement w/o Item', hot: 'w' },
              '-',
              { label: 'Rate Comparison Statement', hot: 'R' },
              '-',
              { label: 'Free Receive Statement', hot: 'F' },
              '-',
              { label: 'Challan Adjustment Report', hot: 'C' },
              { label: 'Purchase Cost Bi-fercation', hot: 'B' },
            ],
          },
          '-',
          { label: 'Item Wise Purchase & %', hot: 'I' },
          '-',
          { label: 'Company Wise Purchase', hot: 'o' },
          { label: 'Purchase Summary', hot: 'S' },
          {
            label: 'P/R, Brk/Exp & ### on Bills', hot: '/', children: [
              { label: 'P/R, Brk/Exp & ### on Bills', hot: 'P', action: 'report.book:PURCHASE_RETURN' },
              { label: 'Monthly ### Report', hot: 'M' },
            ],
          },
          {
            label: 'Item Discount Summary', hot: 'D', children: [
              { label: 'Item Discount Summary', hot: 'I' },
              { label: 'Bill Discount', hot: 'B' },
              { label: 'Bill Discount Summary', hot: 'S' },
            ],
          },
          '-',
          { label: 'Monthly Summaries', hot: 'y' },
          {
            label: 'Item Wise Purchase', hot: 't', children: [
              { label: 'Party/Month Wise', hot: 'P' },
              '-',
              { label: 'Item Wise Purchase', hot: 'I' },
              { label: 'Single Item Purchase', hot: 'S', action: 'item.register' },
              '-',
              { label: 'Party Purchase Summary', hot: 'a' },
              { label: 'Company Purchase Summary', hot: 'C' },
              { label: 'Whole Item Purchase', hot: 'W' },
              '-',
              { label: 'All Items/Parties', hot: 'l' },
              '-',
              { label: 'Item Day Wise Purchase', hot: 'D' },
            ],
          },
          {
            label: 'Company Wise Purchase', hot: 'a', children: [
              { label: 'Party/Month Wise', hot: 'P' },
              { label: 'Items/Month Wise', hot: 'I' },
              { label: 'Company/Month Wise', hot: 'C' },
              '-',
              { label: 'Company Wise Purchase', hot: 'W' },
              { label: 'Single Company Purchase', hot: 'S' },
              '-',
              { label: 'Company Purchase Book', hot: 'B' },
            ],
          },
          {
            label: 'Party Wise Purchase', hot: 'W', children: [
              { label: 'Item/Month Wise', hot: 'I' },
              { label: 'Company/Month Wise', hot: 'C' },
              { label: 'Parties/Month Wise', hot: 'M' },
              '-',
              { label: 'Party Wise Purchase', hot: 'P', action: 'party.txn' },
              { label: 'Single Party Purchase', hot: 'S', action: 'party.txn' },
              '-',
              { label: 'Parties/All Items', hot: 'A' },
              { label: 'Party Wise Lifting', hot: 'L' },
              '-',
              { label: 'PurchaseSale/Collection/O/s', hot: 'O', action: 'report.outstanding:payable' },
            ],
          },
        ],
      },
      {
        label: 'Inventory Report', hot: 'I', children: [
          { label: 'Stock Register Printing', hot: 'R', action: 'item.register' },
          { label: 'Stock & Sale Analysis', hot: 'S', action: 'report.itemsale' },
          { label: 'Stock Ageing Analysis', hot: 'A' },
          { label: "Today's Sale/Purchase & Closing Stock", hot: 'T', action: 'report.mis' },
          { label: 'Negative Stock Checking', hot: 'N' },
          '-',
          { label: 'Whole Special Dealings', hot: 'W' },
          { label: 'Item Special Dealings', hot: 'I' },
          { label: 'Party Special Dealings', hot: 'P' },
          { label: 'Rate Lists', hot: 'L', action: 'item.list' },
          { label: 'List of Scheme/Discount', hot: 'D' },
        ],
      },
      '-',
      {
        label: 'Management Report', hot: 'M', children: [
          {
            label: 'Utilities', hot: 'U', children: [
              {
                label: 'Delete Special Deals', hot: 'D', children: [
                  { label: 'Item', hot: 'I' },
                  { label: 'Company', hot: 'C' },
                  { label: 'Party', hot: 'P' },
                  { label: 'All Items', hot: 'A' },
                  { label: 'All Company', hot: 'l' },
                  { label: 'All Parties', hot: 'r' },
                  { label: 'Company Sp.Rates', hot: 'S' },
                ],
              },
              {
                label: 'Multi Deletion', hot: 'M', children: [
                  { label: 'Company & Non Working Items', hot: 'C' },
                  { label: 'Non Working Salt/HSN/Category', hot: 'S' },
                  { label: 'Duplicate Items', hot: 'D' },
                  { label: 'Non Working Ledgers', hot: 'L' },
                  { label: 'Non Working Sales/Purchase', hot: 'P' },
                  { label: 'Merge Party Same Discounts', hot: 'M' },
                  '-',
                  { label: 'Printer Port Settings', hot: 'r' },
                  { label: 'Bill Headings Settings', hot: 'B' },
                  { label: 'Invoice Formates Settings', hot: 'I' },
                  '-',
                  { label: 'Delete Voucher Create & Modify History', hot: 'V' },
                  { label: 'Company Discount=Master Discount', hot: 'y' },
                  { label: 'Delete Import Data-Receive Wise', hot: 'R' },
                  { label: 'Delete Import Data-Date Wise', hot: 'a' },
                  { label: 'Delete Ledger Transactions', hot: 'T' },
                  { label: 'Delete Opening/Current Stock', hot: 'O' },
                ],
              },
              {
                label: 'Multi Editing', hot: 'E', children: [
                  { label: 'Item Master Multi Editing', hot: 'I' },
                  { label: 'Item Master Manual Edit', hot: 'n' },
                  { label: 'Item/Company Masters', hot: 'C' },
                  { label: 'Delete Company Master Supplier', hot: 'D' },
                  { label: 'Change Company Email Bulk', hot: 'h' },
                  '-',
                  { label: 'Update Stock & Sales MR Panel', hot: 'U' },
                  { label: 'Party Master Multi Editing', hot: 'P' },
                  { label: 'Station shift in Area/Rout', hot: 'S' },
                  { label: 'Party Area/Rout/M.R.', hot: 'a' },
                  { label: 'Party Master', hot: 'y', action: 'ledger.list:Sundry Debtors' },
                  '-',
                  { label: 'Replace Ledger Remarks', hot: 'L' },
                  { label: 'Replace CST/LST Heading with TIN', hot: 'T' },
                  { label: 'Replace Credit Days in Outstanding', hot: 'O' },
                  { label: 'Update Ledger Groups', hot: 'G' },
                  '-',
                  { label: 'Edit Financial Period', hot: 'F' },
                  { label: 'Convert Company Non Batch wise', hot: 'N' },
                  '-',
                  { label: 'Change All Discount with ? %', hot: '?' },
                  { label: 'Tax% & HSN/SAC', hot: 'x', action: 'item.list' },
                  { label: 'Multi Changes in old Bills', hot: 'M' },
                  '-',
                  { label: 'Re-Generate Supplier wise Company', hot: 'R' },
                  { label: 'Re-Arrange My Bill No.', hot: 'A' },
                  { label: 'Re-Save Bill No. Errors', hot: 'v' },
                  { label: 'Re-Numbering My Bill No.', hot: 'b' },
                  { label: 'Re-Arrange My Receipt No.', hot: 'e' },
                  { label: 'Re-Arrange Bill Retail/Tax Heading', hot: 'H' },
                  { label: 'Re-Generate BarCode', hot: 'B' },
                  { label: 'Create Setup of BATCH wise Multiple Barcode', hot: 'E' },
                  { label: 'Update Batch Rates from Last Year', hot: 'Y' },
                  '-',
                  {
                    label: 'Maping', hot: 'p', children: [
                      { label: 'MAPED Items List', hot: 'I' },
                      { label: 'MAPED Parties List', hot: 'P' },
                      '-',
                      { label: 'MAP My Items', hot: 'M' },
                      { label: 'MAP My Codes', hot: 'C' },
                      { label: 'Create MAP File', hot: 'F' },
                      '-',
                      { label: 'Re-Generate Code', hot: 'R' },
                      { label: 'Re-Generate Fast Search Code', hot: 'S' },
                      { label: 'Export Not Re-Generated', hot: 'E' },
                    ],
                  },
                ],
              },
              '-',
              { label: 'Merge Inventory Masters', hot: 'I', children: notShownYet() },
              { label: 'Merge Accounts Masters', hot: 'A', children: notShownYet() },
              '-',
              { label: 'Edit Stock Balances', hot: 'S', children: notShownYet() },
              { label: 'Edit Outstanding', hot: 'O', children: notShownYet() },
              '-',
              { label: 'Re-posting', hot: 'R', children: notShownYet() },
              { label: 'Holidays Setup', hot: 'H' },
              { label: 'Copy', hot: 'C', children: notShownYet() },
              { label: 'Batch Updation', hot: 'B', children: notShownYet() },
              { label: 'Structure/Junk Verification', hot: 'J', children: notShownYet() },
              { label: 'Data Import/Export', hot: 'x', children: notShownYet() },
              { label: 'Create History', hot: 'y', children: notShownYet() },
              { label: 'Voucher Numbering', hot: 'V' },
              { label: 'Group of Accounts', hot: 'G', action: 'group.list' },
              '-',
              { label: 'CARRY BALANCES', hot: 'N' },
              '-',
              { label: 'Misc. Utilities', hot: 'U', children: notShownYet() },
              { label: 'Advance Utilities', hot: 'd' },
            ],
          },
          '-',
          {
            label: 'Gross Profit Analysis', hot: 'G', children: [
              { label: 'G.P. on Consolidated Sale', hot: 'C', action: 'report.gp' },
              { label: 'Gross Profit on Sale', hot: 'S', action: 'report.gp' },
              { label: 'Gross Loss on Sale Return', hot: 'L' },
              '-',
              {
                label: 'Monthly G.P. (Values)', hot: 'M', children: [
                  { label: 'Party G.P. Summary', hot: 'P' },
                  { label: 'M.R. G.P. Summary', hot: 'M' },
                  { label: 'Route G.P. Summary', hot: 'R' },
                  { label: 'Area G.P. Summary', hot: 'A' },
                ],
              },
              '-',
              {
                label: 'Item G.P. Summaries', hot: 'I', children: [
                  { label: 'Party/Month Wise', hot: 'P' },
                  { label: 'M.R./Month Wise', hot: 'M' },
                  { label: 'Area/Month Wise', hot: 'A' },
                  { label: 'Route/Month Wise', hot: 'R' },
                  '-',
                  { label: 'Item Wise G.P.', hot: 'I', action: 'report.gp' },
                  { label: 'Single Item G.P.', hot: 'S' },
                  { label: 'Party G.P. Summary', hot: 'y' },
                  { label: 'M.R. G.P. summary', hot: '.' },
                  { label: 'Route G.P. Summary', hot: 'u' },
                  { label: 'Area G.P. Summary', hot: 'e' },
                  { label: 'Company G.P. Summary', hot: 'C' },
                  '-',
                  { label: 'All Items/Months', hot: 'l' },
                  { label: 'All Items/Parties', hot: 't' },
                  { label: 'All Items/M.R.', hot: '/' },
                  { label: 'All Items/Area', hot: 'r' },
                  { label: 'All Items/Route', hot: 'o' },
                ],
              },
              { label: 'Company G.P. Summary', hot: 'o', children: companySaleMenu('G.P.') },
              {
                label: 'Party G.P. Summaries', hot: 'P', children: [
                  { label: 'Item/Month Wise', hot: 'I' },
                  { label: 'Company/Month Wise', hot: 'C' },
                  '-',
                  { label: 'All Parties/Month', hot: 'A' },
                  { label: 'Party Wise G.P.', hot: 'P' },
                  { label: 'Single Party G.P.', hot: 'S' },
                  '-',
                  { label: 'Parties/All items', hot: 't' },
                ],
              },
              '-',
              { label: 'M.R. G.P. Summaries', hot: 'R', children: regionGpMenu('M.R.', 'M.R. wise G.P.') },
              { label: 'Route G.P. Summaries', hot: 'u', children: regionGpMenu('Route', 'Route Wise G.P.') },
              { label: 'Area G.P. Summaries', hot: 'A', children: regionGpMenu('Area', 'Area Wise G.P.') },
              '-',
              { label: 'Bill Wise Gross Profit', hot: 'B', action: 'report.gp' },
            ],
          },
          {
            label: 'MIS Reports & Graphs', hot: 'M', children: [
              { label: 'Sundry Debtors', hot: 'D', action: 'report.outstanding:receivable' },
              { label: 'Sundry Creditors', hot: 'C', action: 'report.outstanding:payable' },
              { label: 'Expenses', hot: 'E' },
              { label: 'Account Groups', hot: 'A', action: 'report.group' },
              { label: 'Budget Analysis', hot: 'B' },
              { label: 'Debtors Debit/Credit', hot: 'b' },
              { label: 'Party Sale (GP)', hot: 'P' },
              { label: 'Sale Man (GP)', hot: 'S' },
              { label: 'Wholesale (GP)', hot: 'W' },
              { label: 'Product/Party', hot: 'r' },
              { label: 'Product/Sale Man', hot: 'o' },
              { label: 'Party Sale Graphs', hot: 'G' },
            ],
          },
          { label: 'Daily Analysis (MIS)', hot: 'D', action: 'report.mis' },
          { label: 'Company Wise Margin', hot: 'C' },
          { label: 'Master Rates Updation', hot: 'R' },
          '-',
          { label: 'Change Operator Powers', hot: 'O', action: 'role.manage' },
          {
            label: 'Data Transfer Utility', hot: 'T', children: [
              { label: 'Data Transfer Programming', hot: 'D' },
              { label: 'Import Utilities', hot: 'I' },
              '-',
              {
                label: 'Export All Data', hot: 'E', children: [
                  { label: 'DBF', hot: 'D' },
                  { label: 'CSV', hot: 'C' },
                  { label: 'XML', hot: 'X' },
                  { label: 'SQL', hot: 'S' },
                  { label: 'JSON', hot: 'J' },
                ],
              },
              { label: 'Flow Ledger to Flow Ledger Books', hot: 'M' },
              { label: 'Data Re-Save & Posting', hot: 'R' },
            ],
          },
        ],
      },
      {
        label: 'Operator Working', hot: 'O', children: [
          { label: 'Operator Working', hot: 'W' },
          { label: 'Operator Logbook', hot: 'O' },
          { label: 'Server Logbook', hot: 'S' },
          { label: 'SMS Logbook', hot: 'M' },
          { label: 'Email Logbook', hot: 'E' },
          { label: 'Delete Records', hot: 'D' },
          '-',
          { label: 'Important Workbook', hot: 'I' },
          { label: 'Data Recovery Book', hot: 'R' },
        ],
      },
      '-',
      {
        label: 'Accounts Reports', hot: 'A', children: [
          { label: 'Cash & Bank Book', hot: 'C', action: 'report.cashbank' },
          { label: 'All Ledgers', hot: 'A', action: 'report.ledger' },
          '-',
          { label: 'Sundry Debtors', hot: 'D', action: 'report.outstanding:receivable' },
          { label: 'Sundry Creditors', hot: 'r', action: 'report.outstanding:payable' },
          '-',
          { label: 'Expenses', hot: 'E' },
          { label: 'Loan & Advances', hot: 'L' },
          { label: 'Loan Liabilities', hot: 'i' },
          '-',
          { label: 'Sale', hot: 'S', action: 'report.book:SALE,COUNTER_SALE' },
          { label: 'Purchase', hot: 'P', action: 'report.book:PURCHASE' },
          '-',
          { label: 'Depreciation Statement', hot: 'n' },
          { label: 'Monthly Debtors Statement', hot: 'y' },
          '-',
          {
            label: 'Monthly Abstract', hot: 'M', children: [
              { label: 'Debit/Credit Abstract', hot: 'D' },
              { label: 'Month Balance Abstract', hot: 'M' },
              { label: '+/- Abstract Monthly', hot: 'A' },
              { label: '+/- Abstract Day Wise', hot: 'W' },
            ],
          },
          {
            label: 'Bill Adjustment', hot: 'B', children: [
              { label: 'All Bill', hot: 'A' },
              { label: 'Single Party', hot: 'P' },
              { label: 'Single M.R.', hot: 'M' },
              { label: 'Single Route', hot: 'R' },
              { label: 'Single Area', hot: 'e' },
            ],
          },
          { label: 'Memorandum Accounts', hot: 'o' },
          { label: 'Entries Made on Holidays', hot: 'H' },
          { label: 'List of Accounts', hot: 't', action: 'ledger.list' },
        ],
      },
      {
        label: 'Letters & Reminders', hot: 'L', children: [
          { label: 'Payment Reminders', hot: 'P', action: 'report.outstanding:receivable' },
          { label: 'S.T.Forms Reminders', hot: 'S' },
          { label: 'General Letter Writing', hot: 'G' },
          { label: 'General Letter Printing', hot: 'L' },
          { label: 'Item Return Letter', hot: 'I' },
          { label: 'Batch Return Letter', hot: 'B' },
        ],
      },
      {
        label: 'Other Reports', hot: 't', children: [
          { label: 'Telephone/Address/Label', hot: 'T' },
          {
            label: 'M.R./Company/Area/Route', hot: 'M', children: [
              { label: 'M.R.', hot: 'M', action: 'salesman.list' },
              { label: 'Company', hot: 'C' },
              { label: 'Area', hot: 'A' },
              { label: 'Route', hot: 'R' },
              { label: 'Station', hot: 'S' },
            ],
          },
          { label: 'Party/Account List', hot: 'P', action: 'ledger.list' },
          '-',
          { label: 'Collection Reminders', hot: 'C' },
          { label: 'Calendar/Diary', hot: 'D', action: 'calendar' },
          '-',
          { label: 'Order Form', hot: 'O' },
          {
            label: 'Item List', hot: 'I', children: [
              { label: 'Company Wise List', hot: 'C', action: 'item.list' },
              { label: 'Alphabatic List', hot: 'A', action: 'item.list' },
              { label: 'Salt Wise List', hot: 'S' },
            ],
          },
        ],
      },
      '-',
      {
        label: 'Electronic Data Exchange', hot: 'E', children: [
          { label: 'ECOD Secure', hot: 'E' },
          '-',
          { label: 'AWACS', hot: 'A' },
          { label: 'IQVIA', hot: 'I' },
          { label: 'NIELSENIQ', hot: 'N' },
        ],
      },
    ],
  },

  // ====================================================================== HOT KEYS / SYSTEM UTILITIES
  {
    // Several items share an access letter (C, P, S, U) exactly as in MARG; pressing
    // it again moves to the next one.
    label: 'Hot Keys', hot: 'H', children: [
      { label: 'Change Company', hot: 'C', shortcut: 'F1', action: 'company.change' },
      { label: 'Change User', hot: 'U', shortcut: 'CTRL+U', action: 'user.change' },
      '-',
      { label: 'Modify Bill', hot: 'M', shortcut: 'CTRL+F3', action: 'bill.list:SALE' },
      { label: 'Counter Challan', hot: 'C', shortcut: 'ALT+INS', action: 'bill.new:SALE_CHALLAN' },
      { label: 'Item List', hot: 'I', shortcut: 'CTRL+I', action: 'item.list' },
      { label: 'Party List', hot: 'P', shortcut: 'CTRL+L', action: 'report.ledger' },
      '-',
      { label: 'Calendar', hot: 'C', shortcut: 'SHIFT+F12', action: 'calendar' },
      { label: 'Calculator', hot: 'C', shortcut: 'F12', action: 'calc' },
      { label: 'Printer Setup', hot: 'P', shortcut: 'CTRL+F11', action: 'print' },
      { label: 'Shortcuts Help', hot: 'S', shortcut: 'ALT+F1', action: 'shortcuts' },
      '-',
      { label: 'Flush Memory', hot: 'F', shortcut: 'CTRL+S', action: 'refresh' },
      { label: 'Quit', hot: 'Q', shortcut: 'CTRL+Q', action: 'exit' },
    ],
  },

  // ====================================================================== LINKS
  LINKS_MENU,

  // ====================================================================== EXIT
  EXIT_MENU,
];

// MARG reports that Flow Ledger's summary / ageing / stock screens already answer, by
// menu path. Kept apart from the tree above so it stays a copy of MARG's menus.
const S = (spec: string) => `report.summary:${spec}`;
const REPORT_LINKS: Record<string, string> = {
  // Books
  'Books > Entry Books > Selected Book': 'report.daybook',
  'Books > Entry Books > Ledger Day Book': 'report.ledger',
  'Books > Entry Books > Item Day Book': 'item.register',
  'Books > Analytical Summaries > Party wise Sales': S('SALE/party'),
  'Books > Analytical Summaries > Company wise Sales': S('SALE/company'),
  'Books > Outstandings > Ageing': 'report.ageing',
  // Daily Reports → Sale / Purchase / Inventory
  'Daily Reports > Sale Report > M.R. Sale > M.R. Summary': S('SALE/mr'),
  'Daily Reports > Sale Report > Party Sales > Party Summary': S('SALE/party'),
  'Daily Reports > Sale Report > Company/Salt/Category/HSN/SAC > Summary': S('SALE/hsn'),
  'Daily Reports > Sale Report > Company/Salt/Category/HSN/SAC > M.R. Summary': S('SALE/mr'),
  'Daily Reports > Sale Report > Company/Salt/Category/HSN/SAC > Party Summary': S('SALE/party'),
  'Daily Reports > Sale Report > Company/Salt/Category/HSN/SAC > Product Summary': S('SALE/item'),
  'Daily Reports > Sale Report > Day Wise Summary': S('SALE/day'),
  'Daily Reports > Sale Report > Month Wise Summary': S('SALE/month'),
  'Daily Reports > Sale Report > Company/Category wise Tax Summary': S('SALE/company'),
  'Daily Reports > Sale Report > Product wise Purchase': S('PURCHASE/item'),
  'Daily Reports > Purchase Report > Day Wise Summary': S('PURCHASE/day'),
  'Daily Reports > Purchase Report > Month Wise Summary': S('PURCHASE/month'),
  'Daily Reports > Purchase Report > Supplier Wise Statement': S('PURCHASE/party'),
  'Daily Reports > Purchase Report > Company Wise Summary': S('PURCHASE/company'),
  'Daily Reports > Purchase Report > Bill/Item Wise Statement': 'report.book:PURCHASE',
  'Daily Reports > Inventory Reports > Negative Stock Report': 'report.negstock',
  // Reports → Sale Analysis
  'Reports > Sale Analysis > Monthly Summaries > Party Sale Summary': S('SALE/party/month'),
  'Reports > Sale Analysis > Monthly Summaries > M.R. Sale Summary': S('SALE/mr/month'),
  'Reports > Sale Analysis > Item Wise Sale > Party Sale Summary': S('SALE/party'),
  'Reports > Sale Analysis > Item Wise Sale > M.R. Sale Summary': S('SALE/mr'),
  'Reports > Sale Analysis > Item Wise Sale > Company Sale Summary': S('SALE/company'),
  'Reports > Sale Analysis > Item Wise Sale > All Items/Months': S('SALE/item/month'),
  'Reports > Sale Analysis > Company Wise Sale > All Company/Month': S('SALE/company/month'),
  'Reports > Sale Analysis > Company Wise Sale > Company Wise Sale': S('SALE/company'),
  'Reports > Sale Analysis > Party Wise Sale > All Parties/Month': S('SALE/party/month'),
  'Reports > Sale Analysis > Party Wise Sale > Bill Collection Ageing': 'report.ageing',
  'Reports > Sale Analysis > M.R. Wise Sale > All M.R./Month': S('SALE/mr/month'),
  'Reports > Sale Analysis > M.R. Wise Sale > M.R. Wise Sale': 'report.mrsale:summary',
  'Reports > Sale Analysis > M.R. Wise Sale > Single M.R. Sale': 'report.mrsale:single',
  'Reports > Sale Analysis > M.R. Wise Sale > M.R./All Items': 'report.mrsale:items',
  // Reports → Purchase Analysis
  'Reports > Purchase Analysis > Item Wise Purchase & %': S('PURCHASE/item'),
  'Reports > Purchase Analysis > Company Wise Purchase': S('PURCHASE/company'),
  'Reports > Purchase Analysis > Purchase Summary': S('PURCHASE/party'),
  'Reports > Purchase Analysis > Monthly Summaries': S('PURCHASE/party/month'),
  'Reports > Purchase Analysis > Item Wise Purchase > Item Wise Purchase': S('PURCHASE/item'),
  'Reports > Purchase Analysis > Item Wise Purchase > Party Purchase Summary': S('PURCHASE/party'),
  'Reports > Purchase Analysis > Item Wise Purchase > Company Purchase Summary': S('PURCHASE/company'),
  'Reports > Purchase Analysis > Item Wise Purchase > Item Day Wise Purchase': S('PURCHASE/day'),
  'Reports > Purchase Analysis > Company Wise Purchase > Company/Month Wise': S('PURCHASE/company/month'),
  'Reports > Purchase Analysis > Company Wise Purchase > Company Wise Purchase': S('PURCHASE/company'),
  'Reports > Purchase Analysis > Party Wise Purchase > Parties/Month Wise': S('PURCHASE/party/month'),
  // Reports → Inventory / Management (gross profit summaries)
  'Reports > Inventory Report > Negative Stock Checking': 'report.negstock',
  'Reports > Management Report > Gross Profit Analysis > Item G.P. Summaries > Party G.P. Summary': S('GP/party'),
  'Reports > Management Report > Gross Profit Analysis > Item G.P. Summaries > M.R. G.P. summary': S('GP/mr'),
  'Reports > Management Report > Gross Profit Analysis > Item G.P. Summaries > Company G.P. Summary': S('GP/company'),
  'Reports > Management Report > Gross Profit Analysis > Item G.P. Summaries > All Items/Months': S('GP/item/month'),
  'Reports > Management Report > Gross Profit Analysis > Company G.P. Summary > All Company/Month': S('GP/company/month'),
  'Reports > Management Report > Gross Profit Analysis > Company G.P. Summary > Company Wise G.P.': S('GP/company'),
  'Reports > Management Report > Gross Profit Analysis > Party G.P. Summaries > All Parties/Month': S('GP/party/month'),
  'Reports > Management Report > Gross Profit Analysis > Party G.P. Summaries > Party Wise G.P.': S('GP/party'),
  'Reports > Management Report > Gross Profit Analysis > M.R. G.P. Summaries > All M.R./Month': S('GP/mr/month'),
  'Reports > Management Report > Gross Profit Analysis > M.R. G.P. Summaries > M.R. wise G.P.': S('GP/mr'),
  'Reports > Management Report > Gross Profit Analysis > Monthly G.P. (Values) > Party G.P. Summary': S('GP/party/month'),
  'Reports > Management Report > Gross Profit Analysis > Monthly G.P. (Values) > M.R. G.P. Summary': S('GP/mr/month'),
  // GST
  'Gst > Tax Registers & Summaries > Sales Summary': S('SALE/gst'),
  'Gst > Tax Registers & Summaries > Purchase Summary': S('PURCHASE/gst'),
  'Gst > Tax Registers & Summaries > Tax% wise Trading': S('SALE/gst'),
};
(function link(items: MenuEntry[], path: string) {
  for (const m of items) {
    if (m === '-') continue;
    const p = path ? `${path} > ${m.label}` : m.label;
    if (m.children) link(m.children, p);
    else if (!m.action && REPORT_LINKS[p]) m.action = REPORT_LINKS[p];
  }
})(MENU, '');

// The menu bar a USER-role login gets instead of the full one: My Menu, Links, Exit.
export const USER_MENU: MenuItem[] = [
  {
    label: 'My Menu', hot: 'M', children: [
      { label: 'Bill', hot: 'B', action: 'bill.new:SALE' },
      { label: 'Receipt', hot: 'R', action: 'voucher.new:RECEIPT' },
      { label: 'Contra', hot: 'C', action: 'voucher.new:CONTRA' },
      { label: 'Day Book', hot: 'D', action: 'report.daybook' },
      { label: 'Sale Book', hot: 'S', action: 'report.salebook' },
      { label: 'All Ledgers', hot: 'A', action: 'report.ledger' },
      { label: 'Current Stock', hot: 'C', action: 'report.stock' },
      { label: 'SALE RTN', hot: 'L', action: 'bill.new:SALE_RETURN' },
      { label: 'Challan', hot: 'C', action: 'bill.new:PURCHASE_CHALLAN' },   // MARG's My Menu Challan = purchase challan
    ],
  },
  LINKS_MENU,
  EXIT_MENU,
];

// Right-side quick buttons on the main screen.
export const QUICK: { label: string; action: string }[] = [
  { label: 'Sale', action: 'bill.new:SALE' },
  { label: 'Purc.Challan', action: 'bill.new:PURCHASE_CHALLAN' },
  { label: 'S/R Expiry', action: 'bill.new:SALE_RETURN' },
  { label: 'P/R Expiry', action: 'bill.new:PURCHASE_RETURN' },
  { label: 'Receipt', action: 'voucher.new:RECEIPT' },
  { label: 'Payment', action: 'voucher.new:PAYMENT' },
  { label: 'Cash & Bank Book', action: 'report.cashbank' },
  { label: 'Ledger A/c', action: 'report.ledger' },
  { label: 'Outstanding', action: 'report.outstanding:receivable' },
  { label: 'Stock Status', action: 'report.stock' },
  { label: 'Stock & Sales Analysis', action: 'report.itemsale' },
  { label: 'Re-Order', action: 'report.reorder' },
  { label: 'Sales Book', action: 'report.book:SALE,COUNTER_SALE' },
  { label: 'Dispatch Summary', action: 'report.book:SALE' },
  { label: 'Bill Taging', action: 'report.outstanding:receivable' },
  { label: 'Daily Analysis (MIS)', action: 'report.mis' },
  { label: 'Todays Gross Profit', action: 'report.gp' },
  { label: 'Exit', action: 'exit' },
];

// Global shortcuts (Marg's), active on the main screen and over any window.
// Alt+<letter> menu accelerators follow the spec (E T B S D R G F); Alt+A stays
// Counter Sale as in Marg's own shortcut list — Accounts opens with Alt, then A.
export const HOTKEYS: Record<string, string> = {
  'Alt+E': 'menu.open:E',
  'Alt+T': 'menu.open:T',
  'Alt+B': 'menu.open:B',
  'Alt+S': 'menu.open:S',
  'Alt+D': 'menu.open:D',
  'Alt+R': 'menu.open:R',
  'Alt+G': 'menu.open:G',
  'Alt+F': 'menu.open:F',
  'Alt+N': 'bill.new:SALE',
  'Alt+C': 'bill.new:SALE_CHALLAN',
  'Alt+Insert': 'bill.new:SALE_CHALLAN',
  'Alt+A': 'bill.new:COUNTER_SALE',
  'Alt+M': 'bill.list:SALE',
  'Ctrl+F3': 'bill.list:SALE',
  'Alt+P': 'bill.new:PURCHASE',
  'Alt+V': 'voucher.menu',
  'Alt+I': 'voucher.new:RECEIPT',
  'Alt+U': 'report.cashbank',
  'Alt+Q': 'voucher.list:RECEIPT',
  'Alt+L': 'report.ledger',
  'Alt+O': 'report.outstanding:receivable',
  'Alt+X': 'company.change',
  'Ctrl+L': 'report.ledger',
  'Ctrl+I': 'item.list',
  'Ctrl+U': 'user.change',
  'Ctrl+S': 'refresh',
  'F1': 'company.change',
  'F12': 'calc',
  'Shift+F12': 'calendar',
  'Ctrl+F11': 'print',
  'Alt+F1': 'shortcuts',
  'Ctrl+Q': 'exit',
};
